import {
  assoc,
  assocPath,
  chain,
  complement,
  fromPairs,
  groupBy,
  head,
  last,
  mapObjIndexed,
  path,
  prop,
  propEq,
  range,
  sortBy,
} from 'ramda';
import {Option, Options} from '../Util';
import Constants, {FEET_TO_INCHES, ResaleType} from './Constants';
import {Category} from './Tags';
const {AUCTION, CONSIGNMENT, DONATION, RECYCLE} = Constants.ResaleType;

export const ITEM_CATEGORY_SEPARATOR = ' > ';

export type AppItemCategory = {
  item_category_id: number;
  name: string;
  id: number;
};
type DefaultOption<Value> = Option<Value> & {default?: boolean};
export type FacebookCategory =
  | 'Antiques & Collectibles'
  | 'Appliances'
  | 'Arts & Crafts'
  | 'Auto Parts'
  | 'Baby & Kids'
  | 'Bags & Luggage'
  | 'Bicycles'
  | 'Books, Movies & Music'
  | 'Electronics & Computers'
  | 'Furniture'
  | 'Garage Sale'
  | 'Garden'
  | 'Health & Beauty'
  | 'Home Sales'
  | 'Household'
  | 'Jewelry & Accessories'
  | "Men's Clothing & Shoes"
  | 'Miscellaneous'
  | 'Mobile Phones'
  | 'Musical Instruments'
  | 'Pet Supplies'
  | 'Sports & Outdoors'
  | 'Tools'
  | 'Toys & Games'
  | 'Vehicles'
  | 'Video Games'
  | "Women's Clothing & Shoes";
export type OfferUpCategory =
  | 'Antiques'
  | 'Appliances'
  | 'Arts & Crafts'
  | 'Audio Equipment'
  | 'Auto Parts'
  | 'Baby & Kids'
  | 'Beauty & Health'
  | 'Bicycles'
  | 'Books & Magazines'
  | 'Business Equipment'
  | 'CDs & DVDs'
  | 'Cell Phones'
  | 'Clothing & Shoes'
  | 'Collectibles'
  | 'Computer Equipment'
  | 'Computer Software'
  | 'Electronics'
  | 'Farming'
  | 'Furniture'
  | 'Games & Toys'
  | 'General'
  | 'Home & Garden'
  | 'Household'
  | 'Jewelry & Accessories'
  | 'Musical Instruments'
  | 'Pet Supplies'
  | 'Photography'
  | 'Sports & Outdoors'
  | 'Tickets'
  | 'Tools & Machinery'
  | 'TVs'
  | 'Video Equipment'
  | 'Video Games';
export type CategorySustainabilityMetricsFactors = {
  CO2_emissions_avoided_lbsCO2equivalent: Maybe<number>;
  landfill_diverted_lbs: Maybe<number>;
  tree_absorption_equivalent: Maybe<number>;
  water_saved_gallons: Maybe<number>;
  toxic_waste_avoided_lbs: Maybe<number>;
  energy_saved_kWh: Maybe<number>;
  recycling_factor: Maybe<number>;
  multiplier_feature: Maybe<string>;
};
export const EMPTY_SUSTAINABILITY_FACTORS: CategorySustainabilityMetricsFactors = {
  CO2_emissions_avoided_lbsCO2equivalent: null,
  landfill_diverted_lbs: null,
  tree_absorption_equivalent: null,
  water_saved_gallons: null,
  toxic_waste_avoided_lbs: null,
  energy_saved_kWh: null,
  recycling_factor: null,
  multiplier_feature: null,
};
export type CategoryDb = {
  id: number;
  constantName: string;
  name: string;
  facebookName: FacebookCategory;
  offerUpName: OfferUpCategory;
  item_category_id: Maybe<number>;
  package_type: string;
  goodwill_category: string;
  avg_length: number;
  avg_height: number;
  avg_width: number;
  avg_weight: number;
  avg_volume?: number;
  volumes?: DefaultOption<number>[];
  google_product_type: number;
  google_product_type_string: string;
  ebay_id: Maybe<number>;
  ebay_category: string;
  ebay_condition: Maybe<string>;
  donationReceiptDescription: string;
  disassembly_times?: DefaultOption<number>[];
  resaleTypeReasons?: Partial<Record<ResaleType, string[]>>;
};
export type ItemCategory = {
  id: Maybe<number>;
  name: Maybe<string>;
  full_name: Maybe<string>;
  item_category_id: Maybe<number>;
  created_at?: Maybe<string>;
  updated_at?: Maybe<string>;
};
export type ItemCategoryDb = ItemCategory & {
  ebay_id: Maybe<number>;
  package_type: Maybe<string>;
  goodwill_category: Maybe<string>;
  shiphawk_item_type: Maybe<string>;
  editor_price_threshold: Maybe<number>;
  avg_length: Maybe<number>;
  avg_height: Maybe<number>;
  avg_width: Maybe<number>;
  avg_weight: Maybe<number>;
  google_product_type: Maybe<number>;
  google_product_type_string: Maybe<string>;
  ebay_category: Maybe<string>;
  ebay_condition: Maybe<string>;
  shipping_ratio_for_added_piece: Maybe<number>;
  avg_pieces: Maybe<number>;
  curators_employee_ids: number[];
  sustainability_metrics_factors: CategorySustainabilityMetricsFactors;
  /** @deprecated */
  upcycle_wood_consignment_rate: Maybe<number>;
  /** @deprecated */
  upcycle_wood_donation_rate: Maybe<number>;
  /** @deprecated */
  upcycle_wood_recycle_rate: Maybe<number>;
  /** @deprecated */
  upcycle_wood_size_factor: Maybe<number>;
  /** @deprecated */
  upcycle_all_consignment_rate: Maybe<number>;
  /** @deprecated */
  upcycle_all_donation_rate: Maybe<number>;
  /** @deprecated */
  upcycle_all_recycle_rate: Maybe<number>;
};
export const EMPTY_ITEM_CATEGORY: ItemCategory = {
  id: null,
  name: null,
  full_name: null,
  item_category_id: null,
  created_at: null,
  updated_at: null,
};
export const EMPTY_ITEM_CATEGORY_DB: ItemCategoryDb = {
  ...EMPTY_ITEM_CATEGORY,
  ebay_id: null,
  package_type: null,
  goodwill_category: null,
  shiphawk_item_type: null,
  editor_price_threshold: null,
  avg_length: null,
  avg_height: null,
  avg_width: null,
  avg_weight: null,
  google_product_type: null,
  google_product_type_string: null,
  ebay_category: null,
  ebay_condition: null,
  shipping_ratio_for_added_piece: null,
  avg_pieces: null,
  upcycle_wood_consignment_rate: null,
  upcycle_wood_donation_rate: null,
  upcycle_wood_recycle_rate: null,
  upcycle_wood_size_factor: null,
  upcycle_all_consignment_rate: null,
  upcycle_all_donation_rate: null,
  upcycle_all_recycle_rate: null,
  curators_employee_ids: [],
  sustainability_metrics_factors: EMPTY_SUSTAINABILITY_FACTORS,
};
export const CategoriesDb: CategoryDb[] = sortBy(prop('name'), [
  {
    id: 1,
    item_category_id: null,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 436,
    google_product_type_string: 'Furniture',
    ebay_category: '175752',
    ebay_condition: '3000',
    constantName: 'FURNITURE',
    name: 'Furniture',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Furniture may be downgraded if upon inspection we see that the condition has stains, damage or significant scratching.',
      ],
      [CONSIGNMENT]: [
        'Furniture may be downgraded if upon inspection we see that the condition has stains, damage or significant scratching.',
      ],
      [DONATION]: [
        'We are unable to resell furniture pieces that may have limited resale demand due to style, material or condition. Review our consignment criteria.',
        'We are unable to resell items of this style.',
        'We are unable to resell this item due to its condition.',
        'Our appraissers do not think we would be able to resell this item.',
      ],
      [RECYCLE]: [
        'We are unable to resell or donate furniture pieces that are damaged, out of style, made of particle board/veneer.',
        'We are unable to resell or donate items of this style.',
        'We are unable to resell or donate this item due to its condition.',
        'Our appraissers do not think we would be able to resell this item and it is unlikely our donation partners would accept it due to its style/condition.',
      ],
    },
  },
  {
    id: 2,
    item_category_id: 1,
    ebay_id: 175758,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 75,
    avg_height: 18,
    avg_width: 54,
    avg_weight: 80,
    google_product_type: 6433,
    google_product_type_string: 'Furniture > Beds & Accessories',
    ebay_category: '175758',
    ebay_condition: '3000',
    constantName: 'FURNITURE_BEDS',
    name: 'Furniture > Beds',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [DONATION]: ['We are unable to resell bed frames and mattressess.'],
      [RECYCLE]: ['We are unable to resell or donate bed frames and mattressess.'],
    },
  },
  {
    id: 3,
    item_category_id: 1,
    ebay_id: 134648,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 80,
    avg_height: 10,
    avg_width: 60,
    avg_weight: 80,
    google_product_type: 460,
    google_product_type_string: 'Furniture > Sofas',
    ebay_category: '134648',
    ebay_condition: '3000',
    constantName: 'FURNITURE_SOFAS',
    name: 'Furniture > Sofas',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 4,
    item_category_id: 3,
    ebay_id: 134648,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 38,
    avg_height: 72,
    avg_width: 36,
    avg_weight: 200,
    avg_volume: 60,
    google_product_type: 460,
    google_product_type_string: 'Furniture > Sofas',
    ebay_category: '134648',
    ebay_condition: '3000',
    constantName: 'FURNITURE_SOFAS_SLEEPER_SOFA',
    name: 'Furniture > Sofas > Sleeper Sofa',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 122,
    item_category_id: 3,
    ebay_id: 134648,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 80,
    avg_height: 10,
    avg_width: 60,
    avg_weight: 80,
    avg_volume: 60,
    google_product_type: 460,
    google_product_type_string: 'Furniture > Sofas',
    ebay_category: '134648',
    ebay_condition: '3000',
    constantName: 'FURNITURE_SOFAS_3_SEAT',
    name: 'Furniture > Sofas > 3-seat',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 123,
    item_category_id: 3,
    ebay_id: 134648,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 80,
    avg_height: 10,
    avg_width: 60,
    avg_weight: 80,
    avg_volume: 40,
    google_product_type: 460,
    google_product_type_string: 'Furniture > Sofas',
    ebay_category: '134648',
    ebay_condition: '3000',
    constantName: 'FURNITURE_SOFAS_LOVESEAT',
    name: 'Furniture > Sofas > Loveseat',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 124,
    item_category_id: 3,
    ebay_id: 134648,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 80,
    avg_height: 10,
    avg_width: 60,
    avg_weight: 80,
    google_product_type: 460,
    google_product_type_string: 'Furniture > Sofas',
    ebay_category: '134648',
    ebay_condition: '3000',
    constantName: 'FURNITURE_SOFAS_OTHER',
    name: 'Furniture > Sofas > Other',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 5,
    item_category_id: 1,
    ebay_id: 38204,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 43,
    avg_height: 30,
    avg_width: 71,
    avg_weight: 486,
    google_product_type: 6392,
    google_product_type_string: 'Furniture > Tables',
    ebay_category: '38204',
    ebay_condition: '3000',
    constantName: 'FURNITURE_TABLES',
    name: 'Furniture > Tables',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 6,
    item_category_id: 5,
    ebay_id: 38204,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 43,
    avg_height: 30,
    avg_width: 71,
    avg_weight: 486,
    avg_volume: 30,
    google_product_type: 4355,
    google_product_type_string: 'Furniture > Tables > Kitchen & Dining Room Tables',
    ebay_category: '38204',
    ebay_condition: '3000',
    constantName: 'FURNITURE_TABLES_DINING_TABLE',
    name: 'Furniture > Tables > Dining Table',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: 'Dining Table',
  },
  {
    id: 7,
    item_category_id: 5,
    ebay_id: 38204,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 30,
    avg_height: 18,
    avg_width: 45,
    avg_weight: 99,
    avg_volume: 15,
    google_product_type: 1395,
    google_product_type_string: 'Furniture > Tables > Accent Tables > Coffee Tables',
    ebay_category: '38204',
    ebay_condition: '3000',
    constantName: 'FURNITURE_TABLES_COFFEE_TABLE',
    name: 'Furniture > Tables > Coffee Table',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: 'Coffee Table',
  },
  {
    id: 8,
    item_category_id: 5,
    ebay_id: 38204,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 18,
    avg_height: 26,
    avg_width: 18,
    avg_weight: 35,
    avg_volume: 10,
    google_product_type: 1549,
    google_product_type_string: 'Furniture > Tables > Accent Tables > End Tables',
    ebay_category: '38199',
    ebay_condition: '3000',
    constantName: 'FURNITURE_TABLES_END_TABLE',
    name: 'Furniture > Tables > End Table',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: 'End Table',
  },
  {
    id: 9,
    item_category_id: 5,
    ebay_id: 38204,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 30,
    avg_height: 18,
    avg_width: 45,
    avg_weight: 99,
    google_product_type: 6392,
    google_product_type_string: 'Furniture > Tables',
    ebay_category: '38204',
    ebay_condition: '3000',
    constantName: 'FURNITURE_TABLES_OTHER',
    name: 'Furniture > Tables > Other',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 10,
    item_category_id: 1,
    ebay_id: 114397,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 19,
    avg_height: 35,
    avg_width: 50,
    avg_weight: 186,
    avg_volume: 30,
    google_product_type: 4195,
    google_product_type_string: 'Furniture > Cabinets & Storage > Dressers',
    ebay_category: '114397',
    ebay_condition: '3000',
    constantName: 'FURNITURE_DRESSERS',
    name: 'Furniture > Dressers',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 11,
    item_category_id: 1,
    ebay_id: 67172,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 20,
    avg_height: 36,
    avg_width: 22,
    avg_weight: 25,
    google_product_type: 443,
    google_product_type_string: 'Furniture > Chairs',
    ebay_category: '54235',
    ebay_condition: '3000',
    constantName: 'FURNITURE_CHAIRS',
    name: 'Furniture > Chairs',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 12,
    item_category_id: 11,
    ebay_id: 107578,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 18,
    avg_height: 36,
    avg_width: 18,
    avg_weight: 20,
    avg_volume: 10,
    google_product_type: 5886,
    google_product_type_string: 'Furniture > Chairs > Kitchen & Dining Room Chairs',
    ebay_category: '107578',
    ebay_condition: '3000',
    constantName: 'FURNITURE_CHAIRS_DINING_CHAIRS',
    name: 'Furniture > Chairs > Dining Chairs',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: 'Dining Chairs',
  },
  {
    id: 119,
    item_category_id: 11,
    ebay_id: 67172,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 20,
    avg_height: 36,
    avg_width: 22,
    avg_weight: 25,
    avg_volume: 8,
    google_product_type: 443,
    google_product_type_string: 'Furniture > Chairs',
    ebay_category: '54235',
    ebay_condition: '3000',
    constantName: 'FURNITURE_CHAIRS_STOOLS_OTTOMANS_BENCHES',
    name: 'Furniture > Chairs > Stools, Ottomans & Benches',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    volumes: [
      {label: 'Stools', value: 8, default: true},
      {label: 'Ottomans', value: 5},
      {label: 'Benches', value: 15},
    ],
  },
  {
    id: 120,
    item_category_id: 11,
    ebay_id: 67172,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 20,
    avg_height: 36,
    avg_width: 22,
    avg_weight: 25,
    avg_volume: 15,
    google_product_type: 443,
    google_product_type_string: 'Furniture > Chairs',
    ebay_category: '54235',
    ebay_condition: '3000',
    constantName: 'FURNITURE_CHAIRS_LOUNGE',
    name: 'Furniture > Chairs > Lounge',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    volumes: [
      {label: 'Lounge Chair', value: 15, default: true},
      {label: 'Recliner', value: 20},
    ],
  },
  {
    id: 121,
    item_category_id: 11,
    ebay_id: 67172,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 20,
    avg_height: 36,
    avg_width: 22,
    avg_weight: 25,
    google_product_type: 443,
    google_product_type_string: 'Furniture > Chairs',
    ebay_category: '54235',
    ebay_condition: '3000',
    constantName: 'FURNITURE_CHAIRS_OTHER',
    name: 'Furniture > Chairs > Other',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 14,
    item_category_id: 1,
    ebay_id: 11829,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 27,
    avg_height: 60,
    avg_width: 12,
    avg_weight: 65,
    avg_volume: 30,
    google_product_type: 465,
    google_product_type_string: 'Furniture > Shelving > Bookcases & Standing Shelves',
    ebay_category: '3199',
    ebay_condition: '3000',
    constantName: 'FURNITURE_BOOKSHELVES',
    name: 'Furniture > Bookshelves',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    disassembly_times: [
      {label: 'None (0 min)', value: 0, default: true},
      {label: 'Yes (20 min)', value: 20},
    ],
  },
  {
    id: 15,
    item_category_id: 1,
    ebay_id: 134651,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 36,
    avg_height: 18,
    avg_width: 24,
    avg_weight: 150,
    google_product_type: 6356,
    google_product_type_string: 'Furniture > Cabinets & Storage',
    ebay_category: '134651',
    ebay_condition: '3000',
    constantName: 'FURNITURE_STORAGE',
    name: 'Furniture > Storage',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 16,
    item_category_id: 15,
    ebay_id: 42363,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 36,
    avg_height: 18,
    avg_width: 24,
    avg_weight: 150,
    avg_volume: 20,
    google_product_type: 6356,
    google_product_type_string: 'Furniture > Cabinets & Storage',
    ebay_category: '20487',
    ebay_condition: '3000',
    constantName: 'FURNITURE_STORAGE_CABINET',
    name: 'Furniture > Storage > Cabinet',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 18,
    item_category_id: 15,
    ebay_id: 108727,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 22,
    avg_height: 78,
    avg_width: 45,
    avg_weight: 350,
    avg_volume: 30,
    google_product_type: 6358,
    google_product_type_string: 'Furniture > Cabinets & Storage > Media Storage Cabinets & Racks',
    ebay_category: '20488',
    ebay_condition: '3000',
    constantName: 'FURNITURE_STORAGE_ENTERTAINMENT_CENTER',
    name: 'Furniture > Storage > Entertainment Center',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    disassembly_times: [
      {label: 'None (0 min)', value: 0, default: true},
      {label: 'Yes (15 min)', value: 15},
    ],
  },
  {
    id: 19,
    item_category_id: 15,
    ebay_id: 103430,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 60,
    avg_height: 84,
    avg_width: 36,
    avg_weight: 185,
    avg_volume: 80,
    google_product_type: 4063,
    google_product_type_string: 'Furniture > Cabinets & Storage > Armoires & Wardrobes',
    ebay_category: '103430',
    ebay_condition: '3000',
    constantName: 'FURNITURE_STORAGE_HUTCH_ARMOIRE',
    name: 'Furniture > Storage > Hutch & Armoire',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    disassembly_times: [
      {label: 'None (0 min)', value: 0, default: true},
      {label: 'Yes (20 min)', value: 20},
    ],
  },
  {
    id: 20,
    item_category_id: 15,
    ebay_id: 98431,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 20,
    avg_height: 17,
    avg_width: 34,
    avg_weight: 54,
    avg_volume: 10,
    google_product_type: 4205,
    google_product_type_string: 'Furniture > Cabinets & Storage > Storage Chests',
    ebay_category: '125085',
    ebay_condition: '3000',
    constantName: 'FURNITURE_STORAGE_TRUNK',
    name: 'Furniture > Storage > Trunk',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 21,
    item_category_id: 15,
    ebay_id: 175752,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 36,
    avg_height: 18,
    avg_width: 24,
    avg_weight: 150,
    google_product_type: 6356,
    google_product_type_string: 'Furniture > Cabinets & Storage',
    ebay_category: '134651',
    ebay_condition: '3000',
    constantName: 'FURNITURE_STORAGE_OTHER',
    name: 'Furniture > Storage > Other',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 125,
    item_category_id: 15,
    ebay_id: 134651,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 36,
    avg_height: 18,
    avg_width: 24,
    avg_weight: 150,
    avg_volume: 25,
    google_product_type: 6356,
    google_product_type_string: 'Furniture > Cabinets & Storage',
    ebay_category: '134651',
    ebay_condition: '3000',
    constantName: 'FURNITURE_STORAGE_SIDEBOARD_CREDENZA',
    name: 'Furniture > Storage > Sideboard & Credenza',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 126,
    item_category_id: 1,
    ebay_id: 11832,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 24,
    avg_height: 32,
    avg_width: 51,
    avg_weight: 191,
    google_product_type: 4191,
    google_product_type_string: 'Furniture > Office Furniture > Desks',
    ebay_category: '88057',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OFFICE',
    name: 'Furniture > Office',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 13,
    item_category_id: 126,
    ebay_id: 11832,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 24,
    avg_height: 32,
    avg_width: 51,
    avg_weight: 191,
    avg_volume: 25,
    google_product_type: 4191,
    google_product_type_string: 'Furniture > Office Furniture > Desks',
    ebay_category: '88057',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OFFICE_DESKS',
    name: 'Furniture > Office > Desks',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    disassembly_times: [
      {label: 'None (0 min)', value: 0, default: true},
      {label: 'L-shaped (21 min)', value: 21},
      {label: 'Cubicle (37 min)', value: 37},
      {label: 'Pods (63 min)', value: 63},
    ],
  },
  {
    id: 127,
    item_category_id: 126,
    ebay_id: 11832,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 24,
    avg_height: 32,
    avg_width: 51,
    avg_weight: 191,
    avg_volume: 12,
    google_product_type: 4191,
    google_product_type_string: 'Furniture > Office Furniture > Desks',
    ebay_category: '88057',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OFFICE_CHAIRS',
    name: 'Furniture > Office > Chairs',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 128,
    item_category_id: 126,
    ebay_id: 11832,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 24,
    avg_height: 32,
    avg_width: 51,
    avg_weight: 191,
    avg_volume: 15,
    google_product_type: 4191,
    google_product_type_string: 'Furniture > Office Furniture > Desks',
    ebay_category: '88057',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OFFICE_CABINETS',
    name: 'Furniture > Office > Cabinets',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 129,
    item_category_id: 126,
    ebay_id: 11832,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 24,
    avg_height: 32,
    avg_width: 51,
    avg_weight: 191,
    avg_volume: 20,
    google_product_type: 4191,
    google_product_type_string: 'Furniture > Office Furniture > Desks',
    ebay_category: '88057',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OFFICE_TABLES',
    name: 'Furniture > Office > Tables',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    disassembly_times: [
      {label: 'None (0 min)', value: 0},
      {label: 'Conference Table (42 min)', value: 42, default: true},
    ],
    volumes: [
      {label: 'Office Table', value: 20, default: true},
      {label: 'Conference Table', value: 70},
    ],
  },
  {
    id: 130,
    item_category_id: 126,
    ebay_id: 11832,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 24,
    avg_height: 32,
    avg_width: 51,
    avg_weight: 191,
    google_product_type: 4191,
    google_product_type_string: 'Furniture > Office Furniture > Desks',
    ebay_category: '88057',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OFFICE_OTHER',
    name: 'Furniture > Office > Other',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 22,
    item_category_id: 1,
    ebay_id: 175752,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 19,
    avg_height: 35,
    avg_width: 50,
    avg_weight: 186,
    google_product_type: 436,
    google_product_type_string: 'Furniture',
    ebay_category: '175752',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OTHER',
    name: 'Furniture > Other',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 23,
    item_category_id: null,
    ebay_id: null,
    package_type: 'Package Type #3',
    goodwill_category: 'Houseware',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 638,
    google_product_type_string: 'Home & Garden > Kitchen & Dining',
    ebay_category: '177014',
    ebay_condition: '3000',
    constantName: 'KITCHEN',
    name: 'Kitchen',
    facebookName: 'Household',
    offerUpName: 'Household',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [CONSIGNMENT]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [DONATION]: [
        'We are unable to resell kitchen items that may have limited resale demand and that we expect would resell for less than $50. Review our consignment criteria.',
      ],
      [RECYCLE]: [
        'Our appraissers do not think we would be able to resell or donate this item and it is unlikely our donation partners would accept it due to its style/condition.',
      ],
    },
  },
  {
    id: 24,
    item_category_id: 23,
    ebay_id: 36032,
    package_type: 'Package Type #3',
    goodwill_category: 'Houseware',
    avg_length: 6,
    avg_height: 6,
    avg_width: 6,
    avg_weight: 2,
    avg_volume: 5,
    google_product_type: 672,
    google_product_type_string: 'Home & Garden > Kitchen & Dining > Tableware',
    ebay_category: '175802',
    ebay_condition: '3000',
    constantName: 'KITCHEN_DISHWARE',
    name: 'Kitchen > Dishware',
    facebookName: 'Household',
    offerUpName: 'Household',
    donationReceiptDescription: '',
  },
  {
    id: 25,
    item_category_id: 23,
    ebay_id: 20628,
    package_type: 'Package Type #3',
    goodwill_category: 'Houseware',
    avg_length: 8,
    avg_height: 5,
    avg_width: 11,
    avg_weight: 3,
    avg_volume: 5,
    google_product_type: 6070,
    google_product_type_string: 'Home & Garden > Kitchen & Dining > Cookware & Bakeware',
    ebay_category: '20628',
    ebay_condition: '3000',
    constantName: 'KITCHEN_COOKWARE',
    name: 'Kitchen > Cookware',
    facebookName: 'Household',
    offerUpName: 'Household',
    donationReceiptDescription: '',
  },
  {
    id: 26,
    item_category_id: 23,
    ebay_id: 20696,
    package_type: 'Package Type #3',
    goodwill_category: 'Houseware',
    avg_length: 3,
    avg_height: 5,
    avg_width: 3,
    avg_weight: 0,
    avg_volume: 3,
    google_product_type: 672,
    google_product_type_string: 'Home & Garden > Kitchen & Dining > Tableware',
    ebay_category: '20696',
    ebay_condition: '3000',
    constantName: 'KITCHEN_GLASSWARE',
    name: 'Kitchen > Glassware',
    facebookName: 'Household',
    offerUpName: 'Household',
    donationReceiptDescription: '',
  },
  {
    id: 27,
    item_category_id: 23,
    ebay_id: 177004,
    package_type: 'Package Type #3',
    goodwill_category: 'Houseware',
    avg_length: 1,
    avg_height: 3,
    avg_width: 11,
    avg_weight: 0,
    avg_volume: 1,
    google_product_type: 668,
    google_product_type_string: 'Home & Garden > Kitchen & Dining > Kitchen Tools & Utensils',
    ebay_category: '177004',
    ebay_condition: '3000',
    constantName: 'KITCHEN_CUTLERY',
    name: 'Kitchen > Cutlery',
    facebookName: 'Household',
    offerUpName: 'Household',
    donationReceiptDescription: '',
  },
  {
    id: 28,
    item_category_id: 23,
    ebay_id: 177004,
    package_type: 'Package Type #3',
    goodwill_category: 'Houseware',
    avg_length: 8,
    avg_height: 5,
    avg_width: 11,
    avg_weight: 3,
    google_product_type: 638,
    google_product_type_string: 'Home & Garden > Kitchen & Dining',
    ebay_category: '177014',
    ebay_condition: '3000',
    constantName: 'KITCHEN_OTHER',
    name: 'Kitchen > Other',
    facebookName: 'Household',
    offerUpName: 'Household',
    donationReceiptDescription: '',
  },
  {
    id: 29,
    item_category_id: null,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Accesories',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 536,
    google_product_type_string: 'Home & Garden',
    ebay_category: '20715',
    ebay_condition: '3000',
    constantName: 'APPLIANCES',
    name: 'Appliances',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Appliances may be downgraded if they are not : 1) in good and working condition or 2) clean.',
      ],
      [CONSIGNMENT]: [
        'Appliances may be downgraded if they are not : 1) in good and working condition or 2) clean.',
      ],
      [DONATION]: [
        'We are only able to consign clean working appliances that are less than 5 years old from known select brands. Review our consignment criteria.',
      ],
      [RECYCLE]: [
        'Appliances that are old, dirty or not working cannot be resold or donated.',
        'We are unable to resell or donate this type of appliance.',
      ],
    },
  },
  {
    id: 30,
    item_category_id: 29,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Accesories',
    avg_length: 48,
    avg_height: 84,
    avg_width: 96,
    avg_weight: 500,
    google_product_type: 536,
    google_product_type_string: 'Home & Garden',
    ebay_category: '20685',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_SMALL_APPLIANCES',
    name: 'Appliances > Small Appliances',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [DONATION]: [
        'We are only able to consign clean working appliances that are less than 5 years old from known select brands. In general, we accept for resale small appliances that we expect can resell for at least $50. Review our consignment criteria.',
      ],
    },
  },
  {
    id: 31,
    item_category_id: 30,
    ebay_id: 982,
    package_type: 'Package Type #4',
    goodwill_category: 'Accesories',
    avg_length: 16,
    avg_height: 12,
    avg_width: 21,
    avg_weight: 30,
    avg_volume: 3,
    google_product_type: 753,
    google_product_type_string:
      'Home & Garden > Kitchen & Dining > Kitchen Appliances > Microwave Ovens',
    ebay_category: '150140',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_SMALL_APPLIANCES_MICROWAVE',
    name: 'Appliances > Small Appliances > Microwave',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 33,
    item_category_id: 30,
    ebay_id: 65635,
    package_type: 'Package Type #2',
    goodwill_category: 'Accesories',
    avg_length: 8,
    avg_height: 13,
    avg_width: 11,
    avg_weight: 6.5,
    avg_volume: 1,
    google_product_type: 736,
    google_product_type_string:
      'Home & Garden > Kitchen & Dining > Kitchen Appliances > Coffee Makers & Espresso Machines',
    ebay_category: '38252',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_SMALL_APPLIANCES_COFFEE_MAKER',
    name: 'Appliances > Small Appliances > Coffee Maker',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 34,
    item_category_id: 30,
    ebay_id: 150138,
    package_type: 'Package Type #3',
    goodwill_category: 'Accesories',
    avg_length: 48,
    avg_height: 84,
    avg_width: 96,
    avg_weight: 500,
    google_product_type: 1626,
    google_product_type_string: 'Home & Garden > Household Appliances > Climate Control Appliances',
    ebay_category: '20613',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_SMALL_APPLIANCES_HEATER',
    name: 'Appliances > Small Appliances > Heating',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 36,
    item_category_id: 30,
    ebay_id: 3201,
    package_type: 'Package Type #4',
    goodwill_category: 'Accesories',
    avg_length: 11,
    avg_height: 24,
    avg_width: 17,
    avg_weight: 26,
    avg_volume: 5,
    google_product_type: 608,
    google_product_type_string:
      'Home & Garden > Household Appliances > Climate Control Appliances > Fans',
    ebay_category: '20612',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_SMALL_APPLIANCES_FAN',
    name: 'Appliances > Small Appliances > AC & Fans',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 37,
    item_category_id: 30,
    ebay_id: null,
    package_type: 'Package Type #3',
    goodwill_category: 'Accesories',
    avg_length: 48,
    avg_height: 84,
    avg_width: 96,
    avg_weight: 500,
    google_product_type: 536,
    google_product_type_string: 'Home & Garden',
    ebay_category: '20685',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_SMALL_APPLIANCES_OTHER',
    name: 'Appliances > Small Appliances > Other',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 38,
    item_category_id: 29,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Accesories',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 536,
    google_product_type_string: 'Home & Garden',
    ebay_category: '20715',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_LARGE_APPLIANCES',
    name: 'Appliances > Large Appliances',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [DONATION]: [
        'We are only able to consign clean working appliances that are less than 5 years old from known select brands. In general, we accept for resale large appliances that we expect can resell for at least $200. Review our consignment criteria.',
      ],
    },
  },
  {
    id: 39,
    item_category_id: 38,
    ebay_id: 16039,
    package_type: 'None',
    goodwill_category: 'Accesories',
    avg_length: 28,
    avg_height: 67,
    avg_width: 35,
    avg_weight: 357,
    avg_volume: 30,
    google_product_type: 686,
    google_product_type_string:
      'Home & Garden > Kitchen & Dining > Kitchen Appliances > Refrigerators',
    ebay_category: '20713',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_LARGE_APPLIANCES_REFRIGERATOR',
    name: 'Appliances > Large Appliances > Refrigerator',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 40,
    item_category_id: 38,
    ebay_id: 57107,
    package_type: 'None',
    goodwill_category: 'Accesories',
    avg_length: 25,
    avg_height: 34,
    avg_width: 23,
    avg_weight: 78,
    avg_volume: 15,
    google_product_type: 680,
    google_product_type_string:
      'Home & Garden > Kitchen & Dining > Kitchen Appliances > Dishwashers',
    ebay_category: '116023',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_LARGE_APPLIANCES_DISHWASHER',
    name: 'Appliances > Large Appliances > Dishwasher',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 42,
    item_category_id: 38,
    ebay_id: 71257,
    package_type: 'None',
    goodwill_category: 'Accesories',
    avg_length: 27,
    avg_height: 43,
    avg_width: 27,
    avg_weight: 195,
    avg_volume: 25,
    google_product_type: 2706,
    google_product_type_string: 'Home & Garden > Household Appliances > Laundry Appliances',
    ebay_category: '71257',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_LARGE_APPLIANCES_WASHER',
    name: 'Appliances > Large Appliances > Washer/Dryer',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 43,
    item_category_id: 38,
    ebay_id: 117412,
    package_type: 'None',
    goodwill_category: 'Accesories',
    avg_length: 21,
    avg_height: 26,
    avg_width: 34,
    avg_weight: 101,
    avg_volume: 25,
    google_product_type: 730,
    google_product_type_string: 'Home & Garden > Kitchen & Dining > Kitchen Appliances',
    ebay_category: '71250',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_LARGE_APPLIANCES_STOVE_OVEN',
    name: 'Appliances > Large Appliances > Stove & Oven',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 44,
    item_category_id: 38,
    ebay_id: 13596,
    package_type: 'None',
    goodwill_category: 'Accesories',
    avg_length: 27,
    avg_height: 43,
    avg_width: 27,
    avg_weight: 195,
    google_product_type: 536,
    google_product_type_string: 'Home & Garden',
    ebay_category: '20715',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_LARGE_APPLIANCES_OTHER',
    name: 'Appliances > Large Appliances > Other',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 46,
    item_category_id: null,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 988,
    google_product_type_string: 'Sporting Goods',
    ebay_category: '44076',
    ebay_condition: '3000',
    constantName: 'SPORTS_OUTDOORS',
    name: 'Sports & Outdoors',
    facebookName: 'Sports & Outdoors',
    offerUpName: 'Sports & Outdoors',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [CONSIGNMENT]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [DONATION]: [
        'We are unable to resell sport & outdoor items that may have limited resale demand and that we expect would resell for less than $200. Review our consignment criteria.',
      ],
      [RECYCLE]: [
        'Our appraissers do not think we would be able to resell or donate this item and it is unlikely our donation partners would accept it due to its style/condition.',
      ],
    },
  },
  {
    id: 48,
    item_category_id: 46,
    ebay_id: 28067,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 70,
    avg_height: 55,
    avg_width: 32,
    avg_weight: 500,
    avg_volume: 25,
    google_product_type: 990,
    google_product_type_string: 'Sporting Goods > Exercise & Fitness',
    ebay_category: '28063',
    ebay_condition: '3000',
    constantName: 'SPORTS_OUTDOORS_GYM_EQUIPMENT',
    name: 'Sports & Outdoors > Gym Equipment',
    facebookName: 'Sports & Outdoors',
    offerUpName: 'Sports & Outdoors',
    donationReceiptDescription: '',
  },
  {
    id: 49,
    item_category_id: 46,
    ebay_id: 158931,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 2.5,
    avg_height: 27,
    avg_width: 12,
    avg_weight: 5,
    avg_volume: 20,
    google_product_type: 499713,
    google_product_type_string: 'Sporting Goods > Athletics',
    ebay_category: '44076',
    ebay_condition: '3000',
    constantName: 'SPORTS_OUTDOORS_SPORTS_EQUIPMENT',
    name: 'Sports & Outdoors > Sports Equipment',
    facebookName: 'Sports & Outdoors',
    offerUpName: 'Sports & Outdoors',
    donationReceiptDescription: '',
  },
  {
    id: 50,
    item_category_id: 46,
    ebay_id: 158931,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 2.5,
    avg_height: 27,
    avg_width: 12,
    avg_weight: 5,
    google_product_type: 988,
    google_product_type_string: 'Sporting Goods',
    ebay_category: '44076',
    ebay_condition: '3000',
    constantName: 'SPORTS_OUTDOORS_OTHER',
    name: 'Sports & Outdoors > Other',
    facebookName: 'Sports & Outdoors',
    offerUpName: 'Sports & Outdoors',
    donationReceiptDescription: '',
  },
  {
    id: 137,
    item_category_id: 46,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 988,
    google_product_type_string: 'Sporting Goods',
    ebay_category: '44076',
    ebay_condition: '3000',
    constantName: 'SPORTS_OUTDOORS_OUTDOOR_EQUIPMENT',
    name: 'Sports & Outdoors > Outdoor Equipment',
    facebookName: 'Sports & Outdoors',
    offerUpName: 'Sports & Outdoors',
    donationReceiptDescription: '',
  },
  {
    id: 51,
    item_category_id: 56,
    ebay_id: 60432,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 9,
    google_product_type_string: 'Home & Garden > Decor > Artwork',
    ebay_category: '20158',
    ebay_condition: null,
    constantName: 'ART',
    name: 'Home & Decor > Art',
    facebookName: 'Arts & Crafts',
    offerUpName: 'Arts & Crafts',
    donationReceiptDescription: '',
  },
  {
    id: 52,
    item_category_id: 51,
    ebay_id: 60432,
    package_type: 'Package Type #3',
    goodwill_category: 'Other',
    avg_length: 32,
    avg_height: 2,
    avg_width: 20,
    avg_weight: 8,
    avg_volume: 2,
    google_product_type: 9,
    google_product_type_string: 'Home & Garden > Decor > Artwork',
    ebay_category: '551',
    ebay_condition: null,
    constantName: 'HOME_DECOR_ART_DRAWINGS_PAINTINGS',
    name: 'Home & Decor > Art > Drawings & Paintings',
    facebookName: 'Arts & Crafts',
    offerUpName: 'Arts & Crafts',
    donationReceiptDescription: '',
  },
  {
    id: 53,
    item_category_id: 51,
    ebay_id: 60432,
    package_type: 'Package Type #2',
    goodwill_category: 'Other',
    avg_length: 1,
    avg_height: 26,
    avg_width: 30,
    avg_weight: 6,
    avg_volume: 2,
    google_product_type: 500044,
    google_product_type_string:
      'Home & Garden > Decor > Artwork > Posters, Prints, & Visual Artwork',
    ebay_category: '360',
    ebay_condition: null,
    constantName: 'HOME_DECOR_ART_PRINTS_MAPS',
    name: 'Home & Decor > Art > Prints & Maps',
    facebookName: 'Arts & Crafts',
    offerUpName: 'Arts & Crafts',
    donationReceiptDescription: '',
  },
  {
    id: 54,
    item_category_id: 51,
    ebay_id: 60432,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 36,
    avg_height: 14,
    avg_width: 18,
    avg_weight: 2,
    avg_volume: 5,
    google_product_type: 11,
    google_product_type_string: 'Home & Garden > Decor > Artwork > Sculptures & Statues',
    ebay_category: '553',
    ebay_condition: null,
    constantName: 'HOME_DECOR_ART_SCULPTURE',
    name: 'Home & Decor > Art > Sculpture',
    facebookName: 'Arts & Crafts',
    offerUpName: 'Arts & Crafts',
    donationReceiptDescription: '',
  },
  {
    id: 55,
    item_category_id: 51,
    ebay_id: 60432,
    package_type: 'Package Type #4',
    goodwill_category: 'Other',
    avg_length: 32,
    avg_height: 2,
    avg_width: 20,
    avg_weight: 8,
    google_product_type: 9,
    google_product_type_string: 'Home & Garden > Decor > Artwork',
    ebay_category: '20158',
    ebay_condition: null,
    constantName: 'HOME_DECOR_ART_OTHER',
    name: 'Home & Decor > Art > Other',
    facebookName: 'Arts & Crafts',
    offerUpName: 'Arts & Crafts',
    donationReceiptDescription: '',
  },
  {
    id: 56,
    item_category_id: null,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Houseware',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 536,
    google_product_type_string: 'Home & Garden',
    ebay_category: '10034',
    ebay_condition: '3000',
    constantName: 'HOME_DECOR',
    name: 'Home & Decor',
    facebookName: 'Household',
    offerUpName: 'Home & Garden',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [CONSIGNMENT]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [DONATION]: [
        'We are unable to resell decor pieces that may have limited resale demand and that we expect would resell for less than $50. Review our consignment criteria.',
      ],
      [RECYCLE]: [
        'Our appraissers do not think we would be able to resell or donate this item and it is unlikely our donation partners would accept it due to its style/condition.',
      ],
    },
  },
  {
    id: 57,
    item_category_id: 56,
    ebay_id: 81253,
    package_type: 'None',
    goodwill_category: 'Houseware',
    avg_length: 9,
    avg_height: 25,
    avg_width: 11,
    avg_weight: 12,
    avg_volume: 5,
    google_product_type: 594,
    google_product_type_string: 'Home & Garden > Lighting',
    ebay_category: '112581',
    ebay_condition: '3000',
    constantName: 'HOME_DECOR_LIGHTING',
    name: 'Home & Decor > Lighting',
    facebookName: 'Household',
    offerUpName: 'Home & Garden',
    donationReceiptDescription: '',
  },
  {
    id: 58,
    item_category_id: 56,
    ebay_id: 154001,
    package_type: 'None',
    goodwill_category: 'Houseware',
    avg_length: 0,
    avg_height: 64,
    avg_width: 120,
    avg_weight: 22,
    avg_volume: 10,
    google_product_type: 598,
    google_product_type_string: 'Home & Garden > Decor > Rugs',
    ebay_category: '45510',
    ebay_condition: '3000',
    constantName: 'HOME_DECOR_RUG',
    name: 'Home & Decor > Rug',
    facebookName: 'Household',
    offerUpName: 'Home & Garden',
    donationReceiptDescription: '',
  },
  {
    id: 59,
    item_category_id: 56,
    ebay_id: 111264,
    package_type: 'None',
    goodwill_category: 'Houseware',
    avg_length: 1.25,
    avg_height: 31,
    avg_width: 23,
    avg_weight: 10,
    avg_volume: 10,
    google_product_type: 595,
    google_product_type_string: 'Home & Garden > Decor > Mirrors',
    ebay_category: '20580',
    ebay_condition: '3000',
    constantName: 'HOME_DECOR_MIRROR',
    name: 'Home & Decor > Mirror',
    facebookName: 'Household',
    offerUpName: 'Home & Garden',
    donationReceiptDescription: '',
  },
  {
    id: 62,
    item_category_id: 56,
    ebay_id: 66731,
    package_type: 'Package Type #3',
    goodwill_category: 'Houseware',
    avg_length: 5,
    avg_height: 16,
    avg_width: 19,
    avg_weight: 7,
    google_product_type: 569,
    google_product_type_string: 'Home & Garden > Linens & Bedding > Bedding',
    ebay_category: '66737',
    ebay_condition: '3000',
    constantName: 'HOME_DECOR_BEDDING',
    name: 'Home & Decor > Bedding',
    facebookName: 'Household',
    offerUpName: 'Home & Garden',
    donationReceiptDescription: '',
  },
  {
    id: 63,
    item_category_id: 56,
    ebay_id: 10034,
    package_type: 'None',
    goodwill_category: 'Houseware',
    avg_length: 6,
    avg_height: 17,
    avg_width: 12,
    avg_weight: 8,
    google_product_type: 536,
    google_product_type_string: 'Home & Garden',
    ebay_category: '10034',
    ebay_condition: '3000',
    constantName: 'HOME_DECOR_OTHER',
    name: 'Home & Decor > Other',
    facebookName: 'Household',
    offerUpName: 'Home & Garden',
    donationReceiptDescription: '',
  },
  {
    id: 135,
    item_category_id: 56,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Houseware',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 5,
    google_product_type: 536,
    google_product_type_string: 'Home & Garden',
    ebay_category: '10034',
    ebay_condition: '3000',
    constantName: 'HOME_DECOR_CLOCKS',
    name: 'Home & Decor > Clocks',
    facebookName: 'Household',
    offerUpName: 'Home & Garden',
    donationReceiptDescription: '',
  },
  {
    id: 136,
    item_category_id: 56,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Houseware',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 5,
    google_product_type: 536,
    google_product_type_string: 'Home & Garden',
    ebay_category: '10034',
    ebay_condition: '3000',
    constantName: 'HOME_DECOR_VASES',
    name: 'Home & Decor > Vases',
    facebookName: 'Household',
    offerUpName: 'Home & Garden',
    donationReceiptDescription: '',
  },
  {
    id: 64,
    item_category_id: null,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Consumer Electronics',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 222,
    google_product_type_string: 'Electronics',
    ebay_category: '175837',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS',
    name: 'Electronics & Tools',
    facebookName: 'Electronics & Computers',
    offerUpName: 'Electronics',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Electronics may be downgraded if they are: 1) not in good or working condition, 2) missing pieces.',
      ],
      [CONSIGNMENT]: [
        'Electronics may be downgraded if they are: 1) not in good or working condition, 2) missing pieces.',
      ],
      [DONATION]: [
        'Due to the quick pace of technology improvements, we are only able to consign complete working electronics that are less than 5 years old from known select brands that we expect can resell for at least $50. Review our consignment criteria.',
      ],
      [RECYCLE]: [
        'Electronics that are incomplete, dated, dirty or not working cannot be resold or donated.',
        'We are unable to resell or donate this type of electronic.',
      ],
    },
  },
  {
    id: 65,
    item_category_id: 64,
    ebay_id: 162,
    package_type: 'Package Type #2',
    goodwill_category: 'Laptops & Tablets',
    avg_length: 10,
    avg_height: 2,
    avg_width: 14,
    avg_weight: 5,
    avg_volume: 3,
    google_product_type: 278,
    google_product_type_string: 'Electronics > Computers',
    ebay_category: '111422',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_COMPUTERS',
    name: 'Electronics & Tools > Computers',
    facebookName: 'Electronics & Computers',
    offerUpName: 'Computer Equipment',
    donationReceiptDescription: '',
  },
  {
    id: 66,
    item_category_id: 64,
    ebay_id: 21517,
    package_type: 'Package Type #3',
    goodwill_category: 'Monitors',
    avg_length: 1,
    avg_height: 9,
    avg_width: 6,
    avg_weight: 0,
    avg_volume: 3,
    google_product_type: 386,
    google_product_type_string: 'Electronics > Video',
    ebay_category: '80053',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_MONITORS_PROJECTORS',
    name: 'Electronics & Tools > Monitors & Projectors',
    facebookName: 'Electronics & Computers',
    offerUpName: 'Computer Equipment',
    donationReceiptDescription: '',
  },
  {
    id: 67,
    item_category_id: 64,
    ebay_id: 71585,
    package_type: 'None',
    goodwill_category: 'Televisions',
    avg_length: 10,
    avg_height: 28,
    avg_width: 44,
    avg_weight: 50,
    avg_volume: 8,
    google_product_type: 404,
    google_product_type_string: 'Electronics > Video > Televisions',
    ebay_category: '11071',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_TVS',
    name: 'Electronics & Tools > TVs',
    facebookName: 'Electronics & Computers',
    offerUpName: 'TVs',
    donationReceiptDescription: '',
  },
  {
    id: 69,
    item_category_id: 64,
    ebay_id: 102490,
    package_type: 'Package Type #2',
    goodwill_category: 'Consumer Electronics',
    avg_length: 7,
    avg_height: 2,
    avg_width: 11,
    avg_weight: 1,
    google_product_type: 342,
    google_product_type_string: 'Electronics > Networking',
    ebay_category: '44996',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_NETWORKING_EQUIPMENT',
    name: 'Electronics & Tools > Networking Equipment',
    facebookName: 'Electronics & Computers',
    offerUpName: 'Electronics',
    donationReceiptDescription: '',
  },
  {
    id: 70,
    item_category_id: 64,
    ebay_id: 38583,
    package_type: 'Package Type #2',
    goodwill_category: 'Consumer Electronics',
    avg_length: 13,
    avg_height: 6,
    avg_width: 9,
    avg_weight: 4,
    google_product_type: 1294,
    google_product_type_string: 'Electronics > Video Game Consoles',
    ebay_category: '139971',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_GAMING',
    name: 'Electronics & Tools > Gaming',
    facebookName: 'Electronics & Computers',
    offerUpName: 'Video Games',
    donationReceiptDescription: '',
  },
  {
    id: 71,
    item_category_id: 64,
    ebay_id: 168129,
    package_type: 'Package Type #3',
    goodwill_category: 'Consumer Electronics',
    avg_length: 10,
    avg_height: 4,
    avg_width: 10,
    avg_weight: 3,
    avg_volume: 3,
    google_product_type: 223,
    google_product_type_string: 'Electronics > Audio',
    ebay_category: '163829',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_AUDIO_EQUIPMENT',
    name: 'Electronics & Tools > Audio Equipment',
    facebookName: 'Electronics & Computers',
    offerUpName: 'Audio Equipment',
    donationReceiptDescription: '',
  },
  {
    id: 73,
    item_category_id: 64,
    ebay_id: 50336,
    package_type: 'Package Type #1',
    goodwill_category: 'Consumer Electronics',
    avg_length: 4,
    avg_height: 10,
    avg_width: 13,
    avg_weight: 3,
    google_product_type: 142,
    google_product_type_string: 'Cameras & Optics > Cameras',
    ebay_category: '31388',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_CAMERA',
    name: 'Electronics & Tools > Camera',
    facebookName: 'Electronics & Computers',
    offerUpName: 'Video Equipment',
    donationReceiptDescription: '',
  },
  {
    id: 74,
    item_category_id: 64,
    ebay_id: 71485,
    package_type: 'Package Type #1',
    goodwill_category: 'Consumer Electronics',
    avg_length: 6.22,
    avg_height: 3.06,
    avg_width: 0.28,
    avg_weight: 1,
    google_product_type: 262,
    google_product_type_string: 'Electronics > Communications',
    ebay_category: '9355',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_PHONES',
    name: 'Electronics & Tools > Phones/Tablets',
    facebookName: 'Mobile Phones',
    offerUpName: 'Cell Phones',
    donationReceiptDescription: '',
  },
  {
    id: 75,
    item_category_id: 64,
    ebay_id: 46724,
    package_type: 'Package Type #3',
    goodwill_category: 'Consumer Electronics',
    avg_length: 10,
    avg_height: 17,
    avg_width: 20,
    avg_weight: 23,
    avg_volume: 5,
    google_product_type: 500106,
    google_product_type_string:
      'Electronics > Print, Copy, Scan & Fax > Printers, Copiers & Fax Machines',
    ebay_category: '1245',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_PRINTER',
    name: 'Electronics & Tools > Printer',
    facebookName: 'Electronics & Computers',
    offerUpName: 'Computer Equipment',
    donationReceiptDescription: '',
  },
  {
    id: 77,
    item_category_id: 64,
    ebay_id: 3281,
    package_type: 'Package Type #2',
    goodwill_category: 'Consumer Electronics',
    avg_length: 2,
    avg_height: 8,
    avg_width: 12,
    avg_weight: 2,
    google_product_type: 222,
    google_product_type_string: 'Electronics',
    ebay_category: '175837',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_OTHER',
    name: 'Electronics & Tools > Other',
    facebookName: 'Electronics & Computers',
    offerUpName: 'Computer Equipment',
    donationReceiptDescription: '',
  },
  {
    id: 118,
    item_category_id: 64,
    ebay_id: 1268,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 7,
    avg_height: 11,
    avg_width: 19,
    avg_weight: 10,
    avg_volume: 10,
    google_product_type: 111,
    google_product_type_string: 'Business & Industrial',
    ebay_category: '61574',
    ebay_condition: '3000',
    constantName: 'ELECTRONICS_TOOLS_TOOLS',
    name: 'Electronics & Tools > Tools',
    facebookName: 'Tools',
    offerUpName: 'Tools & Machinery',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Tools may be downgraded if they are incomplete, not working, or in bad condition.',
      ],
      [CONSIGNMENT]: [
        'Tools may be downgraded if they are incomplete, not working, or in bad condition.',
      ],
      [DONATION]: [
        'We are unable to resell tools that are very dated, not working, dirty or corroded.',
      ],
      [RECYCLE]: [
        'Tools that are incomplete, dated, dirty or not working cannot be resold or donated.',
        'We are unable to resell or donate this type of tool.',
      ],
    },
  },
  {
    id: 85,
    item_category_id: null,
    ebay_id: null,
    package_type: 'Package Type #1',
    goodwill_category: 'Clothing',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 5,
    google_product_type: 166,
    google_product_type_string: 'Apparel & Accessories',
    ebay_category: '314',
    ebay_condition: '3000',
    constantName: 'APPAREL',
    name: 'Apparel',
    facebookName: "Women's Clothing & Shoes",
    offerUpName: 'Clothing & Shoes',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Items that are stained or with blemishes are likely to be downgraded to donation.',
      ],
      [CONSIGNMENT]: [
        'Items that are stained or with blemishes are likely to be downgraded to donation.',
      ],
      [DONATION]: [
        'We are only able to consign apparel from designer and high-end brands due to the difficulty of processing and shipping these items. In general, we accept for resale apparel pieces that we expect can resell for at least $40. Review list of apparel we typically consign.',
      ],
      [RECYCLE]: [
        'Unfortunately apparel that is damaged/stained is not accepted by donation partners.',
      ],
    },
  },
  {
    id: 86,
    item_category_id: 85,
    ebay_id: null,
    package_type: 'Package Type #1',
    goodwill_category: 'Clothing',
    avg_length: 8,
    avg_height: 2,
    avg_width: 8,
    avg_weight: 0,
    avg_volume: 5,
    google_product_type: 1604,
    google_product_type_string: 'Apparel & Accessories > Clothing',
    ebay_category: '314',
    ebay_condition: '3000',
    constantName: 'APPAREL_CLOTHING',
    name: 'Apparel > Clothing',
    facebookName: "Women's Clothing & Shoes",
    offerUpName: 'Clothing & Shoes',
    donationReceiptDescription: '',
  },
  {
    id: 87,
    item_category_id: 86,
    ebay_id: 313,
    package_type: 'Package Type #1',
    goodwill_category: 'Clothing',
    avg_length: 8,
    avg_height: 2,
    avg_width: 8,
    avg_weight: 0,
    avg_volume: 5,
    google_product_type: 1604,
    google_product_type_string: 'Apparel & Accessories > Clothing',
    ebay_category: '313',
    ebay_condition: '3000',
    constantName: 'APPAREL_CLOTHING_MENS',
    name: 'Apparel > Clothing > Mens',
    facebookName: "Men's Clothing & Shoes",
    offerUpName: 'Clothing & Shoes',
    donationReceiptDescription: '',
  },
  {
    id: 88,
    item_category_id: 86,
    ebay_id: 314,
    package_type: 'Package Type #1',
    goodwill_category: 'Clothing',
    avg_length: 8,
    avg_height: 3,
    avg_width: 8,
    avg_weight: 6,
    avg_volume: 5,
    google_product_type: 1604,
    google_product_type_string: 'Apparel & Accessories > Clothing',
    ebay_category: '314',
    ebay_condition: '3000',
    constantName: 'APPAREL_CLOTHING_WOMENS',
    name: 'Apparel > Clothing > Womens',
    facebookName: "Women's Clothing & Shoes",
    offerUpName: 'Clothing & Shoes',
    donationReceiptDescription: '',
  },
  {
    id: 89,
    item_category_id: 86,
    ebay_id: 1067,
    package_type: 'Package Type #1',
    goodwill_category: 'Clothing',
    avg_length: 12,
    avg_height: 3,
    avg_width: 8,
    avg_weight: 200,
    avg_volume: 5,
    google_product_type: 1604,
    google_product_type_string: 'Apparel & Accessories > Clothing',
    ebay_category: '175640',
    ebay_condition: '3000',
    constantName: 'APPAREL_CLOTHING_CHILDREN',
    name: 'Apparel > Clothing > Children',
    facebookName: 'Baby & Kids',
    offerUpName: 'Baby & Kids',
    donationReceiptDescription: '',
  },
  {
    id: 90,
    item_category_id: 86,
    ebay_id: 155197,
    package_type: 'Package Type #1',
    goodwill_category: 'Clothing',
    avg_length: 8,
    avg_height: 2,
    avg_width: 8,
    avg_weight: 0,
    avg_volume: 5,
    google_product_type: 1604,
    google_product_type_string: 'Apparel & Accessories > Clothing',
    ebay_category: '314',
    ebay_condition: '3000',
    constantName: 'APPAREL_CLOTHING_UNSURE',
    name: 'Apparel > Clothing > Unsure',
    facebookName: "Women's Clothing & Shoes",
    offerUpName: 'Clothing & Shoes',
    donationReceiptDescription: '',
  },
  {
    id: 91,
    item_category_id: 86,
    ebay_id: 91247,
    package_type: 'Package Type #1',
    goodwill_category: 'Clothing',
    avg_length: 8,
    avg_height: 2,
    avg_width: 8,
    avg_weight: 0,
    avg_volume: 5,
    google_product_type: 1604,
    google_product_type_string: 'Apparel & Accessories > Clothing',
    ebay_category: '314',
    ebay_condition: '3000',
    constantName: 'APPAREL_CLOTHING_OTHER',
    name: 'Apparel > Clothing > Other',
    facebookName: "Women's Clothing & Shoes",
    offerUpName: 'Clothing & Shoes',
    donationReceiptDescription: '',
  },
  {
    id: 92,
    item_category_id: 85,
    ebay_id: 166865,
    package_type: 'Package Type #1',
    goodwill_category: 'Shoes',
    avg_length: 12,
    avg_height: 6,
    avg_width: 10,
    avg_weight: 10,
    avg_volume: 5,
    google_product_type: 187,
    google_product_type_string: 'Apparel & Accessories > Shoes',
    ebay_category: '166865',
    ebay_condition: '4000',
    constantName: 'APPAREL_SHOES',
    name: 'Apparel > Shoes',
    facebookName: "Women's Clothing & Shoes",
    offerUpName: 'Clothing & Shoes',
    donationReceiptDescription: '',
  },
  {
    id: 93,
    item_category_id: 85,
    ebay_id: 165254,
    package_type: 'Package Type #1',
    goodwill_category: 'Clothing',
    avg_length: 1,
    avg_height: 1,
    avg_width: 1,
    avg_weight: 0.25,
    avg_volume: 5,
    google_product_type: 188,
    google_product_type_string: 'Apparel & Accessories > Jewelry',
    ebay_category: '98863',
    ebay_condition: '3000',
    constantName: 'APPAREL_JEWELRY',
    name: 'Apparel > Jewelry',
    facebookName: 'Jewelry & Accessories',
    offerUpName: 'Jewelry & Accessories',
    donationReceiptDescription: '',
  },
  {
    id: 94,
    item_category_id: 85,
    ebay_id: 134450,
    package_type: 'Package Type #1',
    goodwill_category: 'Clothing',
    avg_length: 3,
    avg_height: 10,
    avg_width: 14,
    avg_weight: 3,
    avg_volume: 5,
    google_product_type: 6552,
    google_product_type_string: 'Apparel & Accessories > Handbag & Wallet Accessories',
    ebay_category: '169291',
    ebay_condition: '3000',
    constantName: 'APPAREL_PURSES',
    name: 'Apparel > Purses',
    facebookName: 'Bags & Luggage',
    offerUpName: 'Jewelry & Accessories',
    donationReceiptDescription: '',
  },
  {
    id: 95,
    item_category_id: 85,
    ebay_id: null,
    package_type: 'Package Type #1',
    goodwill_category: 'Clothing',
    avg_length: 8,
    avg_height: 3,
    avg_width: 8,
    avg_weight: 6,
    avg_volume: 5,
    google_product_type: 167,
    google_product_type_string: 'Apparel & Accessories > Clothing Accessories',
    ebay_category: '314',
    ebay_condition: '3000',
    constantName: 'APPAREL_OTHER',
    name: 'Apparel > Other',
    facebookName: "Women's Clothing & Shoes",
    offerUpName: 'Clothing & Shoes',
    donationReceiptDescription: '',
  },
  {
    id: 96,
    item_category_id: null,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 8,
    google_product_type_string: 'Arts & Entertainment',
    ebay_category: '88433',
    ebay_condition: null,
    constantName: 'OTHER',
    name: 'Other',
    facebookName: 'Miscellaneous',
    offerUpName: 'General',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [CONSIGNMENT]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [DONATION]: [
        'We are unable to resell items that may have limited resale demand and that we expect would resell for less than $50. Review our consignment criteria.',
      ],
      [RECYCLE]: [
        'Our appraissers do not think we would be able to resell or donate this item and it is unlikely our donation partners would accept it due to its style/condition.',
      ],
    },
  },
  {
    id: 97,
    item_category_id: 96,
    ebay_id: 16080,
    package_type: 'Package Type #3',
    goodwill_category: 'Other',
    avg_length: 8,
    avg_height: 17,
    avg_width: 23.5,
    avg_weight: 18,
    avg_volume: 6,
    google_product_type: 5181,
    google_product_type_string: 'Luggage & Bags',
    ebay_category: '16080',
    ebay_condition: '3000',
    constantName: 'OTHER_LUGGAGE',
    name: 'Other > Luggage',
    facebookName: 'Bags & Luggage',
    offerUpName: 'General',
    donationReceiptDescription: '',
  },
  {
    id: 98,
    item_category_id: 96,
    ebay_id: 47179,
    package_type: 'Package Type #2',
    goodwill_category: 'Other',
    avg_length: 2,
    avg_height: 2,
    avg_width: 2,
    avg_weight: 7900,
    avg_volume: 15,
    google_product_type: 1239,
    google_product_type_string: 'Toys & Games',
    ebay_category: '234',
    ebay_condition: '3000',
    constantName: 'OTHER_KIDS_TOYS',
    name: 'Other > Kids & Toys',
    facebookName: 'Baby & Kids',
    offerUpName: 'Baby & Kids',
    donationReceiptDescription: '',
  },
  {
    id: 99,
    item_category_id: 96,
    ebay_id: 308,
    package_type: 'Package Type #4',
    goodwill_category: 'Other',
    avg_length: 10,
    avg_height: 10,
    avg_width: 10,
    avg_weight: 10,
    avg_volume: 10,
    google_product_type: 54,
    google_product_type_string:
      'Arts & Entertainment > Hobbies & Creative Arts > Musical Instruments',
    ebay_category: '308',
    ebay_condition: '3000',
    constantName: 'OTHER_MUSICAL_INSTRUMENTS_GEAR',
    name: 'Other > Musical Instruments & Gear',
    facebookName: 'Musical Instruments',
    offerUpName: 'Musical Instruments',
    donationReceiptDescription: '',
  },
  {
    id: 100,
    item_category_id: 96,
    ebay_id: 59,
    package_type: 'Package Type #1',
    goodwill_category: 'Other',
    avg_length: 0,
    avg_height: 36,
    avg_width: 24.5,
    avg_weight: 2,
    google_product_type: 783,
    google_product_type_string: 'Media',
    ebay_category: '2312',
    ebay_condition: null,
    constantName: 'OTHER_MEMORABILIA',
    name: 'Other > Memorabilia',
    facebookName: 'Miscellaneous',
    offerUpName: 'General',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [CONSIGNMENT]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [DONATION]: [
        'We are unable to resell items that may have limited resale demand and that we expect would resell for less than $50. Review our consignment criteria.',
      ],
      [RECYCLE]: [
        'Our appraissers do not think we would be able to resell or donate this item and it is unlikely our donation partners would accept it due to its style/condition.',
      ],
    },
  },
  {
    id: 106,
    item_category_id: 96,
    ebay_id: 21097,
    package_type: 'Package Type #2',
    goodwill_category: 'Other',
    avg_length: 36,
    avg_height: 22,
    avg_width: 27,
    avg_weight: 115,
    google_product_type: 2,
    google_product_type_string: 'Animals & Pet Supplies > Pet Supplies',
    ebay_category: '301',
    ebay_condition: '3000',
    constantName: 'OTHER_PET',
    name: 'Other > Pet',
    facebookName: 'Pet Supplies',
    offerUpName: 'Pet Supplies',
    donationReceiptDescription: '',
  },
  {
    id: 107,
    item_category_id: 96,
    ebay_id: 88433,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 7,
    avg_height: 11,
    avg_width: 19,
    avg_weight: 10,
    google_product_type: 8,
    google_product_type_string: 'Arts & Entertainment',
    ebay_category: '88433',
    ebay_condition: null,
    constantName: 'OTHER_OTHER',
    name: 'Other > Other',
    facebookName: 'Miscellaneous',
    offerUpName: 'General',
    donationReceiptDescription: '',
  },
  {
    id: 108,
    item_category_id: null,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 8,
    google_product_type_string: 'Arts & Entertainment',
    ebay_category: '88433',
    ebay_condition: null,
    constantName: 'UNSURE',
    name: 'Unsure',
    facebookName: 'Miscellaneous',
    offerUpName: 'General',
    donationReceiptDescription: '',
  },
  {
    id: 109,
    item_category_id: 5,
    ebay_id: 38204,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 43,
    avg_height: 30,
    avg_width: 71,
    avg_weight: 486,
    avg_volume: 50,
    google_product_type: 6347,
    google_product_type_string: 'Furniture > Furniture Sets > Kitchen & Dining Furniture Sets',
    ebay_category: '107578',
    ebay_condition: '3000',
    constantName: 'FURNITURE_TABLES_DINING_SET',
    name: 'Furniture > Tables > Dining Set',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 110,
    item_category_id: 5,
    ebay_id: 38204,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 43,
    avg_height: 30,
    avg_width: 71,
    avg_weight: 486,
    avg_volume: 25,
    google_product_type: 6369,
    google_product_type_string: 'Furniture > Tables > Accent Tables',
    ebay_category: '183322',
    ebay_condition: '3000',
    constantName: 'FURNITURE_TABLES_SIDE_CONSOLE_TABLE',
    name: 'Furniture > Tables > Side & Console Table',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: 'Side & Console Table',
  },
  {
    id: 111,
    item_category_id: null,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Other',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 30,
    google_product_type: 8,
    google_product_type_string: 'Arts & Entertainment',
    ebay_category: '88433',
    ebay_condition: null,
    constantName: 'MISCELLANEOUS',
    name: 'Miscellaneous',
    facebookName: 'Miscellaneous',
    offerUpName: 'General',
    donationReceiptDescription: '',
    resaleTypeReasons: {
      [AUCTION]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [CONSIGNMENT]: [
        'Items may be downgraded if upon inspection we see that the condition is not good.',
      ],
      [DONATION]: [
        'We are unable to resell items that may have limited resale demand and that we expect would resell for less than $50. Review our consignment criteria.',
      ],
      [RECYCLE]: [
        'Our appraissers do not think we would be able to resell or donate this item and it is unlikely our donation partners would accept it due to its style/condition.',
      ],
    },
  },
  {
    id: 112,
    item_category_id: 2,
    ebay_id: 175758,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 60,
    google_product_type: 6433,
    google_product_type_string: 'Furniture > Beds & Accessories',
    ebay_category: '175758',
    ebay_condition: '3000',
    constantName: 'FURNITURE_BEDS_FRAME_MATTRESS',
    name: 'Furniture > Beds > Frame & Mattress',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    disassembly_times: [
      {label: 'None (0 min)', value: 0},
      {label: 'Yes (21 min)', value: 21, default: true},
    ],
  },
  {
    id: 113,
    item_category_id: 2,
    ebay_id: 175758,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 30,
    google_product_type: 6433,
    google_product_type_string: 'Furniture > Beds & Accessories',
    ebay_category: '175758',
    ebay_condition: '3000',
    constantName: 'FURNITURE_BEDS_FRAME',
    name: 'Furniture > Beds > Frame',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
    disassembly_times: [
      {label: 'None (0 min)', value: 0},
      {label: 'Yes (21 min)', value: 21, default: true},
    ],
  },
  {
    id: 114,
    item_category_id: 2,
    ebay_id: 175758,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 30,
    google_product_type: 6433,
    google_product_type_string: 'Furniture > Beds & Accessories',
    ebay_category: '175758',
    ebay_condition: '3000',
    constantName: 'FURNITURE_BEDS_MATTRESS',
    name: 'Furniture > Beds > Mattress',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 115,
    item_category_id: 3,
    ebay_id: 134648,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 70,
    google_product_type: 460,
    google_product_type_string: 'Furniture > Sofas',
    ebay_category: '134648',
    ebay_condition: '3000',
    constantName: 'FURNITURE_SOFAS_SECTIONAL',
    name: 'Furniture > Sofas > Sectional',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 131,
    item_category_id: 1,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 436,
    google_product_type_string: 'Furniture',
    ebay_category: '175752',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OUTDOOR',
    name: 'Furniture > Outdoor',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 116,
    item_category_id: 131,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 50,
    google_product_type: 436,
    google_product_type_string: 'Furniture',
    ebay_category: '175752',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OUTDOOR_SETS',
    name: 'Furniture > Outdoor > Sets',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 133,
    item_category_id: 131,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 15,
    google_product_type: 436,
    google_product_type_string: 'Furniture',
    ebay_category: '175752',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OUTDOOR_CHAIRS',
    name: 'Furniture > Outdoor > Chairs',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 134,
    item_category_id: 131,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    google_product_type: 436,
    google_product_type_string: 'Furniture',
    ebay_category: '175752',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OUTDOOR_OTHER',
    name: 'Furniture > Outdoor > Other',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 132,
    item_category_id: 131,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 30,
    google_product_type: 436,
    google_product_type_string: 'Furniture',
    ebay_category: '175752',
    ebay_condition: '3000',
    constantName: 'FURNITURE_OUTDOOR_TABLES',
    name: 'Furniture > Outdoor > Tables',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
  {
    id: 117,
    item_category_id: 38,
    ebay_id: null,
    package_type: 'None',
    goodwill_category: 'Accesories',
    avg_length: 0,
    avg_height: 0,
    avg_width: 0,
    avg_weight: 0,
    avg_volume: 20,
    google_product_type: 536,
    google_product_type_string: 'Home & Garden',
    ebay_category: '20715',
    ebay_condition: '3000',
    constantName: 'APPLIANCES_LARGE_APPLIANCES_GRILL',
    name: 'Appliances > Large Appliances > Grill',
    facebookName: 'Appliances',
    offerUpName: 'Appliances',
    donationReceiptDescription: '',
  },
  {
    id: 138,
    item_category_id: 2,
    ebay_id: 109064,
    package_type: 'None',
    goodwill_category: 'Furniture',
    avg_length: 5,
    avg_height: 28,
    avg_width: 56,
    avg_weight: 45,
    avg_volume: 25,
    google_product_type: 451,
    google_product_type_string: 'Furniture > Beds & Accessories > Headboards & Footboards',
    ebay_category: '109064',
    ebay_condition: '3000',
    constantName: 'FURNITURE_BEDS_HEADBOARDS',
    name: 'Furniture > Beds > Headboards',
    facebookName: 'Furniture',
    offerUpName: 'Furniture',
    donationReceiptDescription: '',
  },
]);
export const HashedItemCategories = mapObjIndexed(
  head,
  groupBy(prop('id'), CategoriesDb),
) as Record<string, CategoryDb>;
//
export const ItemCategoriesDb: {
  [key: string]: [string, string, FacebookCategory, OfferUpCategory];
} = fromPairs(
  CategoriesDb.map(
    ({
      id,
      constantName,
      name,
      facebookName,
      offerUpName,
    }): [string, [string, string, FacebookCategory, OfferUpCategory]] => [
      String(id),
      [constantName, name, facebookName, offerUpName],
    ],
  ),
);
export function rootCategoriesAsOptions(): Options<number> {
  return categoriesAsOptions().filter(({label}) => !label.match(ITEM_CATEGORY_SEPARATOR));
}
export function categoriesAsOptions(): Options<number> {
  return CategoriesDb.map(({id, name}) => ({label: name, value: id}));
}
export function disassemblyTimeAsOptions(item_category_id: Maybe<number>): DefaultOption<number>[] {
  if (!item_category_id) return [];
  return CategoriesDb.find(category => category.id === item_category_id)?.disassembly_times || [];
}
export function getDisassemblyDefaultTime(item_category_id: Maybe<number>): Maybe<number> {
  if (!item_category_id) return null;
  return (
    CategoriesDb.find(category => category.id === item_category_id)?.disassembly_times?.find(
      opt => opt.default,
    )?.value || null
  );
}
export function getCategoryVolume(
  item_category_id: Maybe<number>,
  variantLabel?: Maybe<string>,
): Maybe<number> {
  if (!item_category_id) return null;
  const category = CategoriesDb.find(({id}) => id === item_category_id);
  if (!category) return null;
  const variantVolume = variantLabel
    ? category.volumes?.find(({label}) => label === variantLabel)?.value
    : undefined;
  if (variantVolume) return variantVolume;
  const defaultVolume = category.volumes?.find(opt => opt.default)?.value;
  if (defaultVolume) return defaultVolume;
  if (category.avg_volume) return category.avg_volume;
  const {avg_width, avg_height, avg_length} = category;
  if (!avg_width || !avg_height || !avg_length) return null;
  return (avg_width * avg_height * avg_length) / FEET_TO_INCHES ** 3;
}
export function allCategoriesToHash(): {
  id: number;
  name: string;
  item_category_id: number | null | undefined;
}[] {
  const entries: [number, string][] = CategoriesDb.map(({id, name}) => [id, name]);
  return entries.map(([id, fullName]) => {
    const names = fullName.split(ITEM_CATEGORY_SEPARATOR);
    const name = names.pop() || '';
    const parent = names.join(ITEM_CATEGORY_SEPARATOR);
    const parentTuple = entries.find(([_, fullName]) => fullName === parent) || [null];
    return {id, name, item_category_id: parentTuple[0]};
  });
}
export function flatCategories(): {
  id: number;
  parent: number | string;
  text: string;
}[] {
  return allCategoriesToHash().map(({id, name, item_category_id}) => ({
    id,
    text: name,
    parent: item_category_id || '#',
  }));
}
const itemCategoryIdMap: Record<string, number> = Object.entries(ItemCategoriesDb).reduce(
  (acc, [k, v]) => assoc(prop(1, v), parseInt(k), acc),
  {},
);

export function getItemCategoryId(categoriesArray: string[]): number {
  return prop(categoriesArray.join(ITEM_CATEGORY_SEPARATOR), itemCategoryIdMap);
}
export function categoryAscendants(id: number | null | undefined): string[] {
  const category = ItemCategoriesDb[String(id)];
  return category ? category[1].split(ITEM_CATEGORY_SEPARATOR) : [];
}
export function getFacebookCategory(
  categoryFullName: string | null | undefined,
): string | null | undefined {
  const category = CategoriesDb.find(({name}) => name === categoryFullName);
  return category ? category.facebookName : null;
}
export function getOfferUpCategory(
  categoryFullName: string | null | undefined,
): string | null | undefined {
  const category = CategoriesDb.find(({name}) => name === categoryFullName);
  return category ? category.offerUpName : null;
}
export const CategoryMaterials = [
  {
    categories: [
      'FURNITURE_CHAIRS_DINING_CHAIRS',
      'FURNITURE_CHAIRS_STOOLS_OTTOMANS_BENCHES',
      'FURNITURE_CHAIRS_LOUNGE',
      'FURNITURE_CHAIRS_LOUNGE',
      'FURNITURE_OTHER',
      'FURNITURE_SOFAS_SECTIONAL',
      'FURNITURE_SOFAS_SLEEPER_SOFA',
      'FURNITURE_SOFAS_3_SEAT',
      'FURNITURE_SOFAS_LOVESEAT',
      'FURNITURE_SOFAS_OTHER',
      'FURNITURE_TABLES_COFFEE_TABLE',
      'FURNITURE_OFFICE_DESKS',
      'FURNITURE_OFFICE_CHAIRS',
      'FURNITURE_OFFICE_OTHER',
      'FURNITURE_OUTDOOR_CHAIRS',
      'FURNITURE_OUTDOOR_SETS',
      'FURNITURE_OUTDOOR_OTHER',
    ],
    materials: {
      WOOD: 'wood',
      VENEER: 'veneer',
      METAL: 'metal',
      PLASTIC: 'plastic',
      GLASS: 'glass',
      MARBLE: 'marble',
      FAUX_MARBLE_STONE: 'faux marble/stone',
      LEATHER: 'leather',
      PLEATHER: 'pleather',
      FABRIC: 'fabric',
    },
  },
  {
    categories: [
      'FURNITURE_BEDS_FRAME',
      'FURNITURE_BEDS_FRAME_MATTRESS',
      'FURNITURE_BEDS_MATTRESS',
      'FURNITURE_BOOKSHELVES',
      'FURNITURE_DRESSERS',
      'FURNITURE_STORAGE_CABINET',
      'FURNITURE_STORAGE_ENTERTAINMENT_CENTER',
      'FURNITURE_STORAGE_HUTCH_ARMOIRE',
      'FURNITURE_STORAGE_OTHER',
      'FURNITURE_STORAGE_TRUNK',
      'FURNITURE_STORAGE_SIDEBOARD_CREDENZA',
      'FURNITURE_TABLES_DINING_SET',
      'FURNITURE_TABLES_DINING_TABLE',
      'FURNITURE_TABLES_END_TABLE',
      'FURNITURE_TABLES_OTHER',
      'FURNITURE_TABLES_SIDE_CONSOLE_TABLE',
      'FURNITURE_OFFICE_CABINETS',
      'FURNITURE_OFFICE_TABLES',
      'FURNITURE_OUTDOOR_TABLES',
    ],
    materials: {
      WOOD: 'wood',
      VENEER: 'veneer',
      METAL: 'metal',
      PLASTIC: 'plastic',
      GLASS: 'glass',
      MARBLE: 'marble',
      FAUX_MARBLE_STONE: 'faux marble/stone',
    },
  },
  {
    categories: [
      'HOME_DECOR_ART_SCULPTURE',
      'HOME_DECOR_VASES',
      'KITCHEN_GLASSWARE',
      'KITCHEN_DISHWARE',
      'KITCHEN_CUTLERY',
    ],
    materials: {
      CRYSTAL: 'crystal',
      METAL: 'metal',
      WOOD: 'wood',
      PLASTIC: 'plastic',
      PORCELAIN: 'porcelain',
      SILVER: 'silver',
    },
  },
];
export function stringToCategoryId(bluegroundCategory: string): number | null | undefined {
  const standardBluegroundCategory = (bluegroundCategory || '')
    .toLowerCase()
    .trim()
    .replace(/\d\d+/g, ' ');
  const remoovCategory =
    {
      'floor lamp': 'Home & Decor > Lighting',
      'vanity mirror': 'Home & Decor > Mirror',
      'table lamp': 'Home & Decor > Lighting',
      'bedside lamps': 'Home & Decor > Lighting',
      'study desk chair': 'Furniture > Chairs',
      armchair: 'Furniture > Chairs',
      bench: 'Furniture > Chairs > Stools, Ottomans & Benches',
      'bedside tables': 'Furniture > Tables',
      sofa: 'Furniture > Sofas',
      'side table - for armchair': 'Furniture > Tables > Side & Console Table',
      'dining table': 'Furniture > Tables > Dining Table',
      'coffee table': 'Furniture > Tables > Coffee Table',
      'study desk': 'Furniture > Office > Desks',
      'dining chairs': 'Furniture > Chairs > Dining Chairs',
      'leather puff': 'Furniture',
      'side table next to couch': 'Furniture > Tables > Side & Console Table',
      'art work': 'Home & Decor > Art',
      dresser: 'Furniture > Dressers',
      'dressing mirror': 'Home & Decor > Mirror',
      'stool bars': 'Furniture > Chairs > Stools, Ottomans & Benches',
      'couch/ armchair throw': 'Home & Decor',
      'decorative cushions': 'Home & Decor',
      'sofa decorative cushion': 'Home & Decor',
      'artwork - seating area': 'Home & Decor > Art',
      'wine glass': 'Kitchen > Other',
      kitchenware: 'Kitchen > Other',
      'artwork - additional': 'Home & Decor > Art',
      'bar cart': 'Home & Decor',
      'decorative item set': 'Home & Decor',
      'pillows - medium': 'Home & Decor',
      'bath mat': 'Home & Decor',
      'dining rug': 'Home & Decor > Rug',
      'shower curtain': 'Home & Decor',
      'shower curtain hooks': 'Home & Decor',
      bed: 'Furniture > Beds',
      'coat rack': 'Furniture',
      'trash can 40 lt': 'Furniture',
      mattress: 'Furniture',
      'dish rack': 'Home & Decor',
      'towels basket': 'Home & Decor',
      'living room rug': 'Home & Decor > Rug',
      'tv stand': 'Furniture',
      'oven glove': 'Home & Decor',
      'toilet plunger': 'Home & Decor',
      'books for coffee table': 'Home & Decor',
      'ice tray': 'Home & Decor',
    }[standardBluegroundCategory] ||
    bluegroundCategory ||
    '';
  const category = CategoriesDb.find(propEq('name', remoovCategory));
  return category ? category.id : null;
}
export function idToCategory(id: number | null | undefined): string {
  return CategoriesDb.find(propEq('id', id))?.name || '';
}
export function summarizeCategoryName(
  category: string | null | undefined,
): string | null | undefined {
  if (!category) return null;
  return (
    last(category.split(ITEM_CATEGORY_SEPARATOR).filter(complement(propEq('Other')))) || 'Other'
  );
}
export function unsummarizeCategoryName(
  summarizedName: string | null | undefined,
): string[] | null | undefined {
  if (!summarizedName) return null;
  if (summarizedName === 'Other') return null;
  return CategoriesDb.map(prop('name'))
    .find(c => summarizeCategoryName(c) === summarizedName)
    ?.split(ITEM_CATEGORY_SEPARATOR);
}

export const idToCategoryObject = (id: Maybe<number>) => CategoriesDb.find(propEq('id', id));
export const categoryToDonationReceiptDescription = (category?: Maybe<CategoryDb>): string => {
  const categoryNames = category?.name.split(ITEM_CATEGORY_SEPARATOR);
  if (category?.donationReceiptDescription) return category.donationReceiptDescription;
  if (categoryNames?.length === 3) return categoryNames.slice(1).join(' - ');
  if (categoryNames?.length === 2) {
    const lastName = last(categoryNames) as string;
    if (lastName === 'Other') return head(categoryNames) as string;
    return lastName;
  }
  return categoryNames?.[0] || 'Other';
};
export function categoryNameToId(name: Maybe<string>): Maybe<number> {
  return CategoriesDb.find(propEq('name', name))?.id;
}
export function categoryIdToDescendants(categoryId: number): number[] {
  return [
    categoryId,
    ...chain(
      categoryIdToDescendants,
      CategoriesDb.filter(propEq('item_category_id', categoryId)).map(prop('id')),
    ),
  ];
}
export function isElectronics(categoryId: Maybe<number>): boolean {
  if (!categoryId) return false;
  const category = ItemCategoriesDb[categoryId]?.[1];
  if (!category) return false;
  return category.split(ITEM_CATEGORY_SEPARATOR)[0] === 'Electronics & Tools';
}
const SMALL_ELECTRONIC_SUBCATEGORIES = [
  'Computers',
  'Networking Equipment',
  'Gaming',
  'Audio Equipment',
  'Camera',
  'Phones/Tablets',
] as const;
export function isSmallElectronic(categoryId: Maybe<number>): boolean {
  if (!categoryId || !isElectronics(categoryId)) return false;
  const category = ItemCategoriesDb[categoryId]?.[1] || '';
  const subcategory = category.split(ITEM_CATEGORY_SEPARATOR)[1];
  return (SMALL_ELECTRONIC_SUBCATEGORIES as readonly string[]).includes(subcategory);
}
function isRefrigeratorCategory(categoryId: Maybe<number>): boolean {
  if (!categoryId) return false;
  return categoryId === 39;
}
function isMattressCategory(categoryId: Maybe<number>): boolean {
  if (!categoryId) return false;
  const category = ItemCategoriesDb[categoryId]?.[1];
  if (!category) return false;
  return (
    category.split(ITEM_CATEGORY_SEPARATOR)[0] === 'Furniture' && category.includes('Mattress')
  );
}
export type SpecialCategoryFeeKey =
  | 'refrigerator'
  | 'appliances'
  | 'mattress'
  | 'tv'
  | 'smallElectronics'
  | 'largeElectronics';
export function specialCategoryFeeKey(categoryId: Maybe<number>): SpecialCategoryFeeKey | null {
  if (!categoryId) return null;
  if (isRefrigeratorCategory(categoryId)) return 'refrigerator';
  if (isAppliance(categoryId)) return 'appliances';
  if (isMattressCategory(categoryId)) return 'mattress';
  if (categoryId === 67) return 'tv';
  if (isElectronics(categoryId))
    return isSmallElectronic(categoryId) ? 'smallElectronics' : 'largeElectronics';
  return null;
}
export function isApparel(categoryId: Maybe<number>): boolean {
  if (!categoryId) return false;
  const category = ItemCategoriesDb[categoryId]?.[1];
  if (!category) return false;
  return category.split(ITEM_CATEGORY_SEPARATOR)[0] === 'Apparel';
}
export function isFurniture(categoryId: Maybe<number>): boolean {
  if (!categoryId) return false;
  const category = ItemCategoriesDb[categoryId]?.[1];
  if (!category) return false;
  return category.split(ITEM_CATEGORY_SEPARATOR)[0] === 'Furniture';
}
export function isAppliance(categoryId: Maybe<number>): boolean {
  if (!categoryId) return false;
  const category = ItemCategoriesDb[categoryId]?.[1];
  if (!category) return false;
  return category.split(ITEM_CATEGORY_SEPARATOR)[0] === 'Appliances';
}
export function isBed(categoryId: Maybe<number>): boolean {
  if (!categoryId) return false;
  const category = ItemCategoriesDb[categoryId]?.[1];
  if (!category) return false;
  return (
    category.split(ITEM_CATEGORY_SEPARATOR)[1]?.includes('Beds') ||
    category.split(ITEM_CATEGORY_SEPARATOR)[2]?.includes('Sleeper')
  );
}
export function isSofa(categoryId: Maybe<number>): boolean {
  if (!categoryId) return false;
  const category = ItemCategoriesDb[categoryId]?.[1];
  if (!category) return false;
  return category.split(ITEM_CATEGORY_SEPARATOR)[1] === 'Sofas';
}
export function isMirror(categoryId: Maybe<number>) {
  if (!categoryId) return false;
  const category = ItemCategoriesDb[categoryId]?.[1];
  if (!category) return false;
  return category.split(ITEM_CATEGORY_SEPARATOR)[1] === 'Mirror';
}
export function isLamp(categoryId: Maybe<number>) {
  if (!categoryId) return false;
  const category = ItemCategoriesDb[categoryId]?.[1];
  if (!category) return false;
  return category.split(ITEM_CATEGORY_SEPARATOR)[1] === 'Lighting';
}
export function productRequiresShipping(categoryId: Maybe<number>) {
  return !isLamp(categoryId) && !isMirror(categoryId);
}
export function getCategoryById(categoryId: Maybe<number>): Maybe<ItemCategory> {
  if (!categoryId) return null;

  const category = idToCategoryObject(categoryId);
  if (!category) return null;

  return {
    id: categoryId,
    name: last(category?.name.split(ITEM_CATEGORY_SEPARATOR)),
    full_name: category?.name,
    item_category_id: category.item_category_id,
  };
}

export type CategoryTreeInput = {
  id: number;
  text: string;
  parent: number | string;
};
export type CategoryTreeNode = CategoryTreeInput & {
  opened: boolean;
  checked: boolean;
  level: number;
  children: CategoryTreeNode[];
  address?: (string | number)[];
};
export function generateCategoryTree(
  rawCategoryArray: CategoryTreeInput[],
  selectedCategoryIds: number[],
): {address: []; children: CategoryTreeNode[]} {
  const rawCategoryMap: {
    [key: string]: CategoryTreeNode;
  } = rawCategoryArray.reduce((acc, cur) => ({...acc, [cur.id]: cur}), {});
  const level = ({parent}) =>
    rawCategoryMap[String(parent)] ? level(rawCategoryMap[parent]) + 1 : 0;
  const categoryArrayWithLevel: CategoryTreeNode[] = sortBy(
    prop('level'),
    rawCategoryArray.map(node => ({
      ...node,
      children: [],
      level: level(node),
      opened: false,
      checked: selectedCategoryIds.includes(node.id),
    })),
  );
  let result: {address: []; children: CategoryTreeNode[]} = {
    address: [],
    children: [],
  };
  let addresses: {[k: string]: (number | string)[]} = {'#': []};
  const selectedAddresses: (number | string)[][] = [];
  categoryArrayWithLevel.forEach(node => {
    const preAddress = [...addresses[node.parent], 'children'];
    const parent = path(preAddress, result) as CategoryTreeNode[];
    const address = preAddress.concat(parent.length);
    addresses = assoc(String(node.id), address, addresses);
    if (selectedCategoryIds.indexOf(node.id) >= 0) selectedAddresses.push(address);
    result = assocPath(
      address,
      {
        ...node,
        children: [],
        opened: false,
        address: [...address],
        checked: selectedCategoryIds.indexOf(node.id) >= 0,
      },
      result,
    );
  });
  selectedAddresses.map(address =>
    range(1, address.length / 2).map(i => {
      result = assocPath(address.slice(0, i * 2).concat(['opened']), true, result);
    }),
  );
  return result;
}
export function topCategory(category: CategoryDb) {
  return category.name.split(ITEM_CATEGORY_SEPARATOR)[0];
}
export function getMostRelevantCategoryName(categories: Maybe<Category>[]): string {
  const lastCategoryName = last(categories.filter(Boolean))?.name;
  if (!lastCategoryName) return '';
  if (lastCategoryName === 'Other') return getMostRelevantCategoryName(categories.slice(0, -1));
  return lastCategoryName;
}
export type BlindPickupCategories = {
  section: string;
  label: string;
  item_category_id: number;
  ranking: number;
  synonyms: string[];
};
export const CLEAROUT_TYPES = ['Home or Apartment', 'Office', 'Self-Storage Unit'];
export const blindPickupCategories: BlindPickupCategories[] = [
  {
    section: 'Furniture',
    label: '3-Seat Sofa',
    item_category_id: 122,
    ranking: 1,
    synonyms: ['Couch', 'Settee', 'Three-seater', 'Sofa'],
  },
  {
    section: 'Furniture',
    label: 'Lounge Chair',
    item_category_id: 120,
    ranking: 3,
    synonyms: ['Armchair', 'Easy chair', 'Club chair'],
  },
  {
    section: 'Furniture',
    label: 'Recliner',
    item_category_id: 120,
    ranking: 2,
    synonyms: ['Reclining chair', 'La-Z-Boy'],
  },
  {
    section: 'Furniture',
    label: 'Office Desk',
    item_category_id: 13,
    ranking: 4,
    synonyms: ['Work desk', 'Computer desk', 'Writing desk', 'Study table'],
  },
  {
    section: 'Furniture',
    label: 'Dresser',
    item_category_id: 10,
    ranking: 5,
    synonyms: ['Chest of drawers', 'Bureau', 'Drawer chest'],
  },
  {
    section: 'Furniture',
    label: 'End Table',
    item_category_id: 8,
    ranking: 6,
    synonyms: ['Side table', 'Accent table', 'Lamp table'],
  },
  {
    section: 'Furniture',
    label: 'Loveseat Sofa',
    item_category_id: 123,
    ranking: 7,
    synonyms: ['Two-seater sofa', 'Small couch', 'Love seat'],
  },
  {
    section: 'Furniture',
    label: 'Side Table and Console Table',
    item_category_id: 110,
    ranking: 8,
    synonyms: ['Hall table', 'Entryway table', 'Sofa table'],
  },
  {
    section: 'Furniture',
    label: 'Cabinet',
    item_category_id: 16,
    ranking: 9,
    synonyms: ['Cupboard', 'Storage cabinet', 'Locker'],
  },
  {
    section: 'Furniture',
    label: 'Dining Set',
    item_category_id: 109,
    ranking: 10,
    synonyms: ['Table and chairs', 'Dining suite', 'Kitchen set'],
  },
  {
    section: 'Furniture',
    label: 'Sectional Sofa',
    item_category_id: 115,
    ranking: 11,
    synonyms: ['Modular sofa', 'L-shaped couch', 'Corner sofa'],
  },
  {
    section: 'Furniture',
    label: 'Coffee Table',
    item_category_id: 7,
    ranking: 12,
    synonyms: ['Center table', 'Cocktail table', 'Living room table'],
  },
  {
    section: 'Furniture',
    label: 'Stools',
    item_category_id: 119,
    ranking: 13,
    synonyms: ['Bar stool', 'Counter stool'],
  },
  {
    section: 'Furniture',
    label: 'Ottomans',
    item_category_id: 119,
    ranking: 92,
    synonyms: ['Footstool', 'Pouf', 'Footrest'],
  },
  {
    section: 'Furniture',
    label: 'Benches',
    item_category_id: 119,
    ranking: 94,
    synonyms: ['Seat bench', 'Dining bench'],
  },
  {
    section: 'Furniture',
    label: 'Bookshelf',
    item_category_id: 14,
    ranking: 14,
    synonyms: ['Bookcase', 'Book stand', 'Library shelf'],
  },
  {
    section: 'Furniture',
    label: 'Office Chair',
    item_category_id: 127,
    ranking: 15,
    synonyms: ['Desk chair', 'Rolling chair', 'Task chair'],
  },
  {
    section: 'Home & Decor',
    label: 'Lighting',
    item_category_id: 57,
    ranking: 16,
    synonyms: ['Lamp', 'Light fixtures', 'Illumination'],
  },
  {
    section: 'Furniture',
    label: 'Bed Frame',
    item_category_id: 113,
    ranking: 17,
    synonyms: ['Bed base', 'Bedstead', 'Bed structure'],
  },
  {
    section: 'Furniture',
    label: 'Dining Table',
    item_category_id: 6,
    ranking: 18,
    synonyms: ['Kitchen table', 'Meal table'],
  },
  {
    section: 'Furniture',
    label: 'Dining Chair',
    item_category_id: 12,
    ranking: 19,
    synonyms: ['Kitchen chair', 'Side chair'],
  },
  {
    section: 'Home & Decor',
    label: 'Mirror',
    item_category_id: 59,
    ranking: 20,
    synonyms: ['Looking glass', 'Wall mirror', 'Vanity mirror'],
  },
  {
    section: 'Electronics & Tools',
    label: 'TV',
    item_category_id: 67,
    ranking: 21,
    synonyms: ['Television', 'Flat screen', 'Smart TV'],
  },
  {
    section: 'Home & Decor',
    label: 'Rug',
    item_category_id: 58,
    ranking: 22,
    synonyms: ['Carpet', 'Mat', 'Floor covering'],
  },
  {
    section: 'Furniture',
    label: 'Entertainment Center',
    item_category_id: 18,
    ranking: 23,
    synonyms: ['Media console', 'TV stand', 'Wall unit'],
  },
  {
    section: 'Furniture',
    label: 'Other Storage',
    item_category_id: 21,
    ranking: 24,
    synonyms: ['Storage unit', 'Storage box', 'Organizer'],
  },
  {
    section: 'Furniture',
    label: 'Office Cabinet',
    item_category_id: 128,
    ranking: 25,
    synonyms: ['Filing cabinet', 'Office storage', 'Drawer cabinet'],
  },
  {
    section: 'Furniture',
    label: 'Mattress',
    item_category_id: 114,
    ranking: 26,
    synonyms: ['Bed mattress', 'Foam mattress', 'Spring mattress'],
  },
  {
    section: 'Furniture',
    label: 'Sleeper Sofa',
    item_category_id: 4,
    ranking: 27,
    synonyms: ['Sofa bed', 'Pullout couch', 'Convertible sofa'],
  },
  {
    section: 'Furniture',
    label: 'Other Chair',
    item_category_id: 121,
    ranking: 28,
    synonyms: ['Seating', 'Chair', 'Armchair'],
  },
  {
    section: 'Sports & Outdoors',
    label: 'Gym Equipment',
    item_category_id: 48,
    ranking: 29,
    synonyms: ['Exercise machine', 'Fitness equipment', 'Workout gear'],
  },
  {
    section: 'Furniture',
    label: 'Sideboard and Credenza',
    item_category_id: 125,
    ranking: 30,
    synonyms: ['Buffet', 'Console', 'Dining storage'],
  },
  {
    section: 'Furniture',
    label: 'Hutch and Armoire',
    item_category_id: 19,
    ranking: 31,
    synonyms: ['Wardrobe', 'Closet', 'Cupboard'],
  },
  {
    section: 'Appliances',
    label: 'Refrigerator',
    item_category_id: 39,
    ranking: 32,
    synonyms: ['Fridge', 'Icebox', 'Cooler'],
  },
  {
    section: 'Home & Decor',
    label: 'Art Prints and Maps',
    item_category_id: 53,
    ranking: 33,
    synonyms: ['Posters', 'Wall art', 'Maps'],
  },
  {
    section: 'Home & Decor',
    label: 'Other',
    item_category_id: 63,
    ranking: 34,
    synonyms: ['Miscellaneous decor', 'Decorative items'],
  },
  {
    section: 'Furniture',
    label: 'Outdoor Set',
    item_category_id: 116,
    ranking: 35,
    synonyms: ['Patio set', 'Garden furniture', 'Outdoor suite'],
  },
  {
    section: 'Furniture',
    label: 'Outdoor Chair',
    item_category_id: 133,
    ranking: 36,
    synonyms: ['Patio chair', 'Garden chair', 'Deck chair'],
  },
  {
    section: 'Appliances',
    label: 'Washer and Dryer',
    item_category_id: 42,
    ranking: 37,
    synonyms: ['Laundry machine', 'Washing machine', 'Clothes dryer'],
  },
  {
    section: 'Home & Decor',
    label: 'Art Drawings and Paintings',
    item_category_id: 52,
    ranking: 38,
    synonyms: ['Canvas', 'Sketches', 'Wall paintings'],
  },
  {
    section: 'Furniture',
    label: 'Other Table',
    item_category_id: 9,
    ranking: 39,
    synonyms: ['Miscellaneous table', 'Small table'],
  },
  {
    section: 'Other',
    label: 'Other',
    item_category_id: 107,
    ranking: 40,
    synonyms: ['Miscellaneous', 'Uncategorized'],
  },
  {
    section: 'Furniture',
    label: 'Headboard',
    item_category_id: 138,
    ranking: 41,
    synonyms: ['Bed head', 'Bed panel', 'Bed backrest'],
  },
  {
    section: 'Furniture',
    label: 'Other',
    item_category_id: 22,
    ranking: 42,
    synonyms: ['Miscellaneous furniture', 'Uncategorized furniture'],
  },
  {
    section: 'Other',
    label: 'Kids and Toys',
    item_category_id: 98,
    ranking: 43,
    synonyms: ['Children toys', 'Playthings', 'Games'],
  },
  {
    section: 'Furniture',
    label: 'Other Sofa',
    item_category_id: 124,
    ranking: 44,
    synonyms: ['Miscellaneous couch', 'Other couch'],
  },
  {
    section: 'Appliances',
    label: 'AC and Fans',
    item_category_id: 36,
    ranking: 45,
    synonyms: ['Air conditioner', 'Cooling fan', 'Ceiling fan'],
  },
  {
    section: 'Furniture',
    label: 'Office Table',
    item_category_id: 129,
    ranking: 46,
    synonyms: ['Work table', 'Meeting table'],
  },
  {
    section: 'Furniture',
    label: 'Conference Table',
    item_category_id: 129,
    ranking: 95,
    synonyms: ['Boardroom table', 'Meeting table'],
  },
  {
    section: 'Home & Decor',
    label: 'Vase',
    item_category_id: 136,
    ranking: 47,
    synonyms: ['Flower vase', 'Decorative jar', 'Pot'],
  },
  {
    section: 'Electronics & Tools',
    label: 'Monitor and Projector',
    item_category_id: 66,
    ranking: 48,
    synonyms: ['Computer monitor', 'Projector', 'Screen display'],
  },
  {
    section: 'Home & Decor',
    label: 'Other Art',
    item_category_id: 55,
    ranking: 49,
    synonyms: ['Miscellaneous art', 'Decorative art'],
  },
  {
    section: 'Appliances',
    label: 'Microwave',
    item_category_id: 31,
    ranking: 50,
    synonyms: ['Microwave oven', 'Microwave cooker'],
  },
  {
    section: 'Appliances',
    label: 'Stove and Oven',
    item_category_id: 43,
    ranking: 51,
    synonyms: ['Cooktop', 'Range', 'Cooking stove'],
  },
  {
    section: 'Electronics & Tools',
    label: 'Audio Equipment',
    item_category_id: 71,
    ranking: 52,
    synonyms: ['Speakers', 'Sound system', 'Stereo'],
  },
  {
    section: 'Kitchen',
    label: 'Glassware',
    item_category_id: 26,
    ranking: 53,
    synonyms: ['Drinking glasses', 'Tumblers', 'Cups'],
  },
  {
    section: 'Kitchen',
    label: 'Dishware',
    item_category_id: 24,
    ranking: 54,
    synonyms: ['Plates', 'Bowls', 'Tableware'],
  },
  {
    section: 'Electronics & Tools',
    label: 'Printer',
    item_category_id: 75,
    ranking: 55,
    synonyms: ['Copy machine', 'Print device', 'Laser printer'],
  },
  {
    section: 'Furniture',
    label: 'Outdoor Table',
    item_category_id: 132,
    ranking: 56,
    synonyms: ['Patio table', 'Garden table', 'Deck table'],
  },
  {
    section: 'Furniture',
    label: 'Trunk',
    item_category_id: 20,
    ranking: 57,
    synonyms: ['Chest', 'Storage trunk', 'Footlocker'],
  },
  {
    section: 'Home & Decor',
    label: 'Bedding',
    item_category_id: 62,
    ranking: 58,
    synonyms: ['Bed sheets', 'Blankets', 'Linens'],
  },
  {
    section: 'Sports & Outdoors',
    label: 'Outdoor Equipment',
    item_category_id: 137,
    ranking: 59,
    synonyms: ['Camping gear', 'Hiking gear', 'Outdoor gear'],
  },
  {
    section: 'Home & Decor',
    label: 'Clock',
    item_category_id: 135,
    ranking: 60,
    synonyms: ['Wall clock', 'Alarm clock', 'Timepiece'],
  },
  {
    section: 'Electronics & Tools',
    label: 'Other',
    item_category_id: 77,
    ranking: 61,
    synonyms: ['Miscellaneous electronics', 'Other tools'],
  },
  {
    section: 'Kitchen',
    label: 'Cookware',
    item_category_id: 25,
    ranking: 62,
    synonyms: ['Pots', 'Pans', 'Cooking utensils'],
  },
  {
    section: 'Furniture',
    label: 'Storage',
    item_category_id: 15,
    ranking: 63,
    synonyms: ['Storage unit', 'Organizer', 'Container'],
  },
  {
    section: 'Sports & Outdoors',
    label: 'Sports Equipment',
    item_category_id: 49,
    ranking: 64,
    synonyms: ['Athletic gear', 'Exercise equipment', 'Sporting goods'],
  },
  {
    section: 'Appliances',
    label: 'Coffee Maker',
    item_category_id: 33,
    ranking: 65,
    synonyms: ['Coffee machine', 'Espresso maker', 'Brewer'],
  },
  {
    section: 'Furniture',
    label: 'Other Outdoor',
    item_category_id: 134,
    ranking: 66,
    synonyms: ['Miscellaneous patio furniture', 'Outdoor item'],
  },
  {
    section: 'Furniture',
    label: 'Other Office',
    item_category_id: 130,
    ranking: 67,
    synonyms: ['Miscellaneous office furniture', 'Office item'],
  },
  {
    section: 'Appliances',
    label: 'Grill',
    item_category_id: 117,
    ranking: 68,
    synonyms: ['Barbecue', 'BBQ grill', 'Charcoal grill'],
  },
  {
    section: 'Apparel',
    label: 'Other Clothing',
    item_category_id: 91,
    ranking: 69,
    synonyms: ['Miscellaneous clothes', 'Garments'],
  },
  {
    section: 'Home & Decor',
    label: 'Art Sculpture',
    item_category_id: 54,
    ranking: 70,
    synonyms: ['Statue', 'Carving', 'Decorative figure'],
  },
  {
    section: 'Other',
    label: 'Musical Instruments and Gear',
    item_category_id: 99,
    ranking: 71,
    synonyms: ['Instruments', 'Music gear', 'Band equipment'],
  },
  {
    section: 'Appliances',
    label: 'Heater',
    item_category_id: 34,
    ranking: 72,
    synonyms: ['Radiator', 'Space heater', 'Heating device'],
  },
  {
    section: 'Other',
    label: 'Pet',
    item_category_id: 106,
    ranking: 73,
    synonyms: ['Animal supplies', 'Pet gear'],
  },
  {
    section: 'Appliances',
    label: 'Dishwasher',
    item_category_id: 40,
    ranking: 74,
    synonyms: ['Dish washer', 'Dish cleaning machine'],
  },
  {
    section: 'Electronics & Tools',
    label: 'Tools',
    item_category_id: 118,
    ranking: 75,
    synonyms: ['Hand tools', 'Power tools', 'Tool kit'],
  },
  {
    section: 'Kitchen',
    label: 'Cutlery',
    item_category_id: 27,
    ranking: 76,
    synonyms: ['Knives', 'Forks', 'Spoons', 'Flatware'],
  },
  {
    section: 'Sports & Outdoors',
    label: 'Other',
    item_category_id: 50,
    ranking: 77,
    synonyms: ['Miscellaneous sports', 'Outdoor gear'],
  },
  {
    section: 'Apparel',
    label: "Women's Clothing",
    item_category_id: 88,
    ranking: 78,
    synonyms: ['Ladies wear', 'Female apparel'],
  },
  {
    section: 'Apparel',
    label: 'Shoes',
    item_category_id: 92,
    ranking: 79,
    synonyms: ['Footwear', 'Sneakers', 'Boots'],
  },
  {
    section: 'Kitchen',
    label: 'Other',
    item_category_id: 28,
    ranking: 80,
    synonyms: ['Miscellaneous kitchenware', 'Kitchen item'],
  },
  {
    section: 'Other',
    label: 'Luggage',
    item_category_id: 97,
    ranking: 81,
    synonyms: ['Suitcase', 'Bag', 'Travel case'],
  },
  {
    section: 'Electronics & Tools',
    label: 'Computer',
    item_category_id: 65,
    ranking: 82,
    synonyms: ['PC', 'Laptop', 'Desktop'],
  },
  {
    section: 'Apparel',
    label: "Men's Clothing",
    item_category_id: 87,
    ranking: 83,
    synonyms: ['Male wear', 'Menswear'],
  },
  {
    section: 'Apparel',
    label: 'Other',
    item_category_id: 95,
    ranking: 84,
    synonyms: ['Miscellaneous apparel', 'Other garments'],
  },
  {
    section: 'Apparel',
    label: 'Purse',
    item_category_id: 94,
    ranking: 85,
    synonyms: ['Handbag', 'Clutch', 'Pocketbook'],
  },
  {
    section: 'Electronics & Tools',
    label: 'Gaming',
    item_category_id: 70,
    ranking: 86,
    synonyms: ['Video games', 'Console gaming', 'Game systems'],
  },
  {
    section: 'Electronics & Tools',
    label: 'Networking Equipment',
    item_category_id: 69,
    ranking: 87,
    synonyms: ['Router', 'Switch', 'Modem'],
  },
  {
    section: 'Electronics & Tools',
    label: 'Camera',
    item_category_id: 73,
    ranking: 88,
    synonyms: ['Photo camera', 'Digital camera', 'DSLR'],
  },
  {
    section: 'Other',
    label: 'Memorabilia',
    item_category_id: 100,
    ranking: 89,
    synonyms: ['Collectibles', 'Souvenirs', 'Keepsakes'],
  },
  {
    section: 'Electronics & Tools',
    label: 'Phone and Tablet',
    item_category_id: 74,
    ranking: 90,
    synonyms: ['Smartphone', 'Cellphone', 'iPad'],
  },
  {
    section: 'Apparel',
    label: 'Jewelry',
    item_category_id: 93,
    ranking: 91,
    synonyms: ['Necklace', 'Ring', 'Bracelet'],
  },
  {
    section: 'Apparel',
    label: "Children's Clothing",
    item_category_id: 89,
    ranking: 93,
    synonyms: ['Kids wear', 'Baby clothes', 'Toddler clothing'],
  },
];
